<template>
  <div>
    <v-dialog persistent v-model="internalDialog" max-width="1200">
      <v-card class="bus-change-dialog">
        <v-card-title class="dialog-header">
          <v-icon class="mr-3" color="white" size="28">mdi-bus-clock</v-icon>
          <span class="dialog-title">{{ dialogTitle }}</span>
          <v-spacer></v-spacer>
          <v-btn class="white--text" icon dark @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-3 dialog-body">
          <!-- Ended Bus Change banner -->
          <v-card v-if="isBusChangeEnded" flat class="ended-bus-change-card mb-3">
            <div class="ended-bus-change-header">
              <v-avatar color="rgba(255,255,255,0.18)" size="34" class="mr-3">
                <v-icon dark size="18">mdi-flag-checkered</v-icon>
              </v-avatar>
              <div class="ended-bus-change-title-block">
                <div class="ended-bus-change-title">
                  {{ endedBannerTitle }}
                  <span class="ended-bus-change-date" v-if="busChange.endDate">
                    &middot; {{ formatDateOnly(busChange.endDate) }}
                  </span>
                </div>
              </div>
              <v-spacer></v-spacer>
              <v-btn
                v-if="!isViewOnly && editingEndInfo"
                small
                outline
                dark
                class="my-0 mr-1 ended-bus-change-btn"
                style="min-width: 0; padding: 0 8px;"
                @click="cancelEndInfoEdit"
              >
                <v-icon left small>mdi-close</v-icon>
                Cancel
              </v-btn>
              <v-btn
                v-if="!isViewOnly"
                small
                outline
                dark
                class="my-0 ended-bus-change-btn"
                style="min-width: 0; padding: 0 8px;"
                :loading="savingEndInfo"
                @click="toggleEndInfoEdit"
              >
                <v-icon left small>{{
                  editingEndInfo ? "mdi-check" : "mdi-pencil"
                }}</v-icon>
                {{ editingEndInfo ? "Done" : "Edit" }}
              </v-btn>
            </div>

            <div class="ended-bus-change-body">
              <div v-if="!editingEndInfo" class="ended-bus-change-rows">
                <div class="ended-bus-change-row">
                  <v-icon small class="mr-2">mdi-bus</v-icon>
                  <span v-if="endedReturnRoute || endedReturnStopNumber">
                    Return on
                    <strong v-if="endedReturnRoute">
                      Bus #{{ getRouteBusNumber(endedReturnRoute) || "-" }}
                      <span v-if="endedReturnRoute.color">({{ endedReturnRoute.color }})</span>
                    </strong>
                    <span v-if="endedReturnStopNumber">
                      &middot; Stop {{ endedReturnStopNumber }}
                    </span>
                  </span>
                  <span v-else class="ended-bus-change-missing">
                    No return route/stop on file
                  </span>
                </div>
                <div class="ended-bus-change-row" v-if="studentOriginalBusAddress">
                  <v-icon small class="mr-2">mdi-home-map-marker</v-icon>
                  <span>
                    Original Address:
                    <strong>{{ studentOriginalBusAddress }}</strong>
                  </span>
                </div>
              </div>

              <div v-else class="ended-bus-change-edit">
                <v-autocomplete
                  v-model="endEditReturnRoute"
                  :items="filteredDropoffRoutes"
                  :filter="customRouteFilter"
                  item-text="description"
                  item-value="id"
                  label="Return Route"
                  prepend-inner-icon="mdi-bus-clock"
                  outlined
                  dense
                  hide-details
                  class="route-autocomplete mb-2 ended-bus-change-field"
                  placeholder="Start typing to search..."
                  return-object
                  @change="onEndEditReturnRouteChange"
                  :menu-props="{ bottom: true, offsetY: true }"
                >
                  <template v-slot:selection="{ item }">
                    <div class="route-selection-wrapper">
                      <div class="route-selection-main">
                        {{ item.description }}
                        <span
                          v-if="getRouteBusNumber(item)"
                          class="route-detail-chip"
                          :style="getRouteChipStyle(item)"
                        >
                          Bus #{{ getRouteBusNumber(item) }}
                        </span>
                      </div>
                    </div>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-tile-content
                      class="route-list-item"
                      :style="
                        getRouteColor(item.color)
                          ? `border-left: 4px solid ${getRouteColor(item.color)}`
                          : ''
                      "
                    >
                      <v-list-tile-title>
                        <strong>{{ item.description }}</strong>
                      </v-list-tile-title>
                      <v-list-tile-sub-title>
                        <span v-if="getRouteBusNumber(item)">
                          Bus #{{ getRouteBusNumber(item) }}
                        </span>
                      </v-list-tile-sub-title>
                    </v-list-tile-content>
                  </template>
                </v-autocomplete>

                <v-autocomplete
                  v-if="endEditReturnRoute"
                  v-model="endEditReturnStopNumber"
                  :items="endEditReturnRouteStops"
                  item-text="label"
                  item-value="stopNumber"
                  label="Return Stop"
                  prepend-inner-icon="mdi-map-marker-radius"
                  outlined
                  dense
                  hide-details
                  class="ended-bus-change-field"
                  :loading="loadingEndReturnStops"
                  :error="!!endEditReturnRoute && !endEditReturnStopNumber"
                  placeholder="Select stop on this bus..."
                  :menu-props="{ bottom: true, offsetY: true, maxHeight: 320 }"
                >
                  <template v-slot:selection="{ item }">
                    <span class="geo-stop-selection">
                      <span class="geo-stop-badge">{{ item.stopNumber }}</span>
                      <span class="geo-stop-address">{{
                        item.stopAddress || item.label
                      }}</span>
                    </span>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-tile-content>
                      <v-list-tile-title class="geo-stop-item-title">
                        <span class="geo-stop-badge">{{ item.stopNumber }}</span>
                        {{ item.stopAddress }}
                      </v-list-tile-title>
                    </v-list-tile-content>
                  </template>
                </v-autocomplete>
              </div>
            </div>
          </v-card>

          <!-- Selected Student Card -->
          <div v-if="busChange">
            <v-card class="selected-student-card mb-4" outlined>
              <v-card-text class="pa-0">
                <v-layout align-center>
                  <v-avatar color="rgb(92, 184, 96)" size="40" class="mr-2">
                    <v-icon dark size="24">mdi-account</v-icon>
                  </v-avatar>
                  <v-flex>
                    <v-layout row wrap>
                      <v-flex xs12 sm6 class="pr-2">
                        <div class="body-2 font-weight-medium pb-0">
                          {{ busChange.studentNameHeb || busChange.studentName }}
                          <span
                            class="caption grey--text ml-1"
                            v-if="
                              busChange.studentNameHeb && busChange.studentName
                            "
                          >
                            ({{ busChange.studentName }})
                          </span>
                        </div>
                        <div class="caption grey--text">
                          <strong>Student ID:</strong>
                          {{ busChange.studentID }} |
                          <strong>Building:</strong>
                          {{ viewBuilding }} | <strong>Class:</strong>
                          {{ viewClass }}-{{ viewSection }}
                        </div>
                        <div
                          v-if="
                            isDayCampView &&
                              dayCampEnrollment &&
                              dayCampEnrollment.regType
                          "
                          class="caption daycamp-enrollment-line"
                        >
                          <v-icon x-small color="rgb(92, 184, 96)" class="mr-1"
                            >mdi-tent</v-icon
                          >
                          {{ getEnrollmentDisplay(dayCampEnrollment) }}
                        </div>
                      </v-flex>
                      <v-flex xs12 sm6>
                        <div class="caption grey--text" v-if="viewTeacher">
                          <v-icon x-small class="mr-1">mdi-account-tie</v-icon>
                          {{ viewTeacher }}
                        </div>
                        <div
                          class="caption grey--text"
                          v-else-if="
                            isDayCampView &&
                              dayCampEnrollment &&
                              dayCampEnrollment.room
                          "
                        >
                          <v-icon x-small class="mr-1">mdi-door</v-icon>
                          Room: {{ dayCampEnrollment.room }}
                        </div>
                        <div
                          class="caption grey--text"
                          v-if="busChange.homeAddress"
                        >
                          <v-icon x-small class="mr-1">mdi-home</v-icon>
                          {{ busChange.homeAddress }}
                        </div>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                </v-layout>
              </v-card-text>
            </v-card>
          </div>

          <v-layout v-if="busChange" row wrap>
            <v-flex xs12 md3 class="pr-2">
              <v-card
                v-if="playableRecordingUrl"
                outlined
                class="pa-2 original-request-card mb-2"
              >
                <div class="recording-header">
                  <v-icon small color="#ff6b6b" class="recording-icon"
                    >mdi-microphone</v-icon
                  >
                  <span class="recording-label">Original Request</span>
                </div>
                <audio
                  ref="audioPlayer"
                  controls
                  controlsList="nodownload"
                  :src="playableRecordingUrl"
                  class="inline-audio-player mt-1"
                ></audio>
              </v-card>
              <v-card outlined class="pa-2 compact-info">
                <div class="section-header mb-2">
                  <v-icon x-small class="mr-1">mdi-account</v-icon>
                  Info
                </div>
                <div class="info-row">
                  <span class="info-label">Student:</span>
                  <span class="info-value">{{
                    busChange.studentName || "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">ID:</span>
                  <span class="info-value">{{
                    busChange.studentID || "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Family ID:</span>
                  <span class="info-value">{{ infoFamilyId || "-" }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Source:</span>
                  <span class="info-value">{{ busChange.source || "-" }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Entered:</span>
                  <span class="info-value">{{
                    formatDateTime(busChange.enteredOn)
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Entered By:</span>
                  <span class="info-value">{{
                    busChange.enteredBy || "-"
                  }}</span>
                </div>
                <div class="info-row" v-if="linkedRequestCallerID">
                  <span class="info-label">Caller ID:</span>
                  <span class="info-value">{{
                    formatPhoneNumber(linkedRequestCallerID)
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Home:</span>
                  <span class="info-value">{{
                    busChange.homePhone || "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Cell:</span>
                  <span class="info-value">{{
                    busChange.cellPhone || "-"
                  }}</span>
                </div>
              </v-card>
              <v-card outlined class="pa-2 mt-2 form-section-card">
                <div class="d-flex align-center mb-2">
                  <v-spacer></v-spacer>
                  <v-btn
                    v-if="!isViewOnly && editingExtraInfo"
                    small
                    outline
                    color="grey darken-1"
                    class="my-0 mr-1"
                    style="min-width: 0; padding: 0 8px;"
                    @click="cancelExtraInfoEdit"
                  >
                    <v-icon left small>mdi-close</v-icon>
                    Cancel
                  </v-btn>
                  <v-btn
                    v-if="!isViewOnly"
                    small
                    outline
                    :color="editingExtraInfo ? 'success' : 'grey darken-1'"
                    class="my-0 ml-0"
                    style="min-width: 0; padding: 0 8px;"
                    :loading="savingExtraInfo"
                    @click="toggleExtraInfoEdit"
                  >
                    <v-icon left small>{{
                      editingExtraInfo ? "mdi-check" : "mdi-pencil"
                    }}</v-icon>
                    {{ editingExtraInfo ? "Done" : "Edit" }}
                  </v-btn>
                </div>
                <div v-if="!editingExtraInfo || isViewOnly">
                  <div class="info-row">
                    <span class="info-label">Geo Phone:</span>
                    <span class="info-value">{{
                      formData.geoPhoneNumber || "-"
                    }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Note:</span>
                    <span class="info-value info-value-full">{{
                      formData.note || "-"
                    }}</span>
                  </div>
                </div>
                <div v-else>
                  <v-text-field
                    v-model="formData.geoPhoneNumber"
                    label="Geo Tracking Phone"
                    prepend-inner-icon="mdi-phone-in-talk"
                    outlined
                    dense
                    hide-details
                    class="mb-2"
                    mask="###-###-####"
                    autocomplete="no-password"
                  ></v-text-field>
                  <v-textarea
                    v-model="formData.note"
                    label="Note"
                    prepend-inner-icon="mdi-note-text"
                    outlined
                    dense
                    hide-details
                    rows="2"
                    autocomplete="no-password"
                  ></v-textarea>
                </div>
              </v-card>
            </v-flex>

            <!-- Bus Change Form Sections -->
            <v-flex xs12 md6 class="px-2">
              <!-- Dates -->
              <v-card outlined class="pa-3 mb-3 form-section-card">
                <div class="section-header mb-2">
                  <v-icon small class="mr-1">mdi-calendar-range</v-icon>
                  Dates
                  <v-spacer></v-spacer>
                  <v-btn
                    v-if="!isViewOnly"
                    small
                    color="success"
                    outline
                    class="my-0"
                    @click="setTodayOnlyAfternoon"
                  >
                    <v-icon left small>mdi-weather-sunset</v-icon>
                    Today Only Afternoon
                  </v-btn>
                </div>
                <v-layout row wrap>
                  <v-flex xs12 sm6 class="pr-2">
                    <div class="caption period-title mb-1">Starting Run</div>
                    <v-menu
                      v-model="startDateMenu"
                      :close-on-content-click="false"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-model="formData.startDate"
                          label="Start Date"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          outlined
                          dense
                          hide-details
                          class="mb-2"
                          v-on="on"
                          :disabled="isViewOnly"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="formData.startDate"
                        color="rgb(92, 184, 96)"
                        @input="startDateMenu = false"
                      ></v-date-picker>
                    </v-menu>
                    <v-select
                      v-model="formData.startPeriod"
                      :items="periodOptions"
                      item-text="label"
                      item-value="value"
                      label="Run *"
                      prepend-inner-icon="mdi-weather-sunset-up"
                      outlined
                      dense
                      hide-details
                      :disabled="isViewOnly"
                      :rules="[rules.required]"
                    ></v-select>
                  </v-flex>
                  <v-flex xs12 sm6 class="pl-2">
                    <div class="caption period-title mb-1">Ending After</div>
                    <v-menu
                      v-model="endDateMenu"
                      :close-on-content-click="false"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-model="formData.endDate"
                          label="End Date"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          outlined
                          dense
                          hide-details
                          class="mb-2"
                          v-on="on"
                          :disabled="isViewOnly"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="formData.endDate"
                        color="rgb(92, 184, 96)"
                        :min="formData.startDate"
                        @input="endDateMenu = false"
                      ></v-date-picker>
                    </v-menu>
                    <v-select
                      v-model="formData.endPeriod"
                      :items="periodOptions"
                      item-text="label"
                      item-value="value"
                      label="Run *"
                      prepend-inner-icon="mdi-weather-sunset-down"
                      outlined
                      dense
                      hide-details
                      :disabled="isViewOnly"
                      :rules="[rules.required]"
                    ></v-select>
                  </v-flex>
                </v-layout>
                <div
                  class="caption grey--text text--darken-1 mt-3 mb-2 period-helper-text"
                >
                  Morning Pickup: Includes the trip TO school. Afternoon
                  Dropoff: Includes the trip FROM school.
                </div>
                <v-alert
                  v-if="deadlineWarningMessage"
                  type="warning"
                  outline
                  dense
                  value="true"
                  class="mb-2 pa-2"
                >
                  {{ deadlineWarningMessage }}
                </v-alert>
                <v-alert
                  :value="!!enrollmentDateWarning"
                  type="warning"
                  outline
                  dense
                  class="mb-0 pa-2"
                >
                  {{ enrollmentDateWarning }}
                </v-alert>
              </v-card>

              <!-- Destination Info -->
              <v-card outlined class="pa-3 mb-3 form-section-card">
                <div class="section-header mb-3">
                  <v-icon small class="mr-1">mdi-map-marker</v-icon>
                  Destination Info
                </div>
                <v-text-field
                  v-model="formData.busChangeAddress"
                  label="Destination Address *"
                  prepend-inner-icon="mdi-map-marker"
                  outlined
                  dense
                  hide-details
                  :readonly="isViewOnly"
                  autocomplete="no-password"
                  class="mb-2"
                ></v-text-field>
                <v-layout row wrap class="mb-2">
                  <v-flex xs12 sm8 class="pr-1">
                    <v-text-field
                      v-model="formData.busChangePhoneNumber"
                      label="Destination Phone"
                      prepend-inner-icon="mdi-phone"
                      outlined
                      dense
                      hide-details
                      :readonly="isViewOnly"
                      mask="###-###-####"
                      autocomplete="no-password"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm4 class="pl-1">
                    <v-btn
                      v-if="!isViewOnly"
                      color="success"
                      outline
                      block
                      class="mt-0"
                      :disabled="!formData.busChangePhoneNumber"
                      @click="searchAccountByPhone"
                    >
                      <v-icon left small>mdi-magnify</v-icon>
                      Search
                    </v-btn>
                  </v-flex>
                </v-layout>
                <v-text-field
                  v-model="formData.busChangeName"
                  label="Family Name"
                  prepend-inner-icon="mdi-account"
                  outlined
                  dense
                  hide-details
                  :readonly="isViewOnly"
                  autocomplete="no-password"
                ></v-text-field>
                <div
                  v-if="formData.busChangeAccountID"
                  class="caption grey--text mt-1 mb-2"
                >
                  Account ID: {{ formData.busChangeAccountID }}
                </div>
                <v-layout row wrap class="mt-2">
                  <v-flex xs12 sm6 class="pr-2">
                    <v-text-field
                      v-model="formData.notificationPhoneNumber"
                      label="Notification Phone"
                      prepend-inner-icon="mdi-bell"
                      outlined
                      dense
                      :readonly="isViewOnly"
                      mask="###-###-####"
                      autocomplete="no-password"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm6 class="pl-2">
                    <v-select
                      v-model="formData.notificationType"
                      :items="['MESSAGE', 'CALL']"
                      label="Notification Type"
                      prepend-inner-icon="mdi-message-alert"
                      outlined
                      dense
                      clearable
                      :disabled="isViewOnly"
                    ></v-select>
                  </v-flex>
                </v-layout>
              </v-card>

              <!-- Bus -->
              <v-card outlined class="pa-3 mb-1 form-section-card">
                <div class="section-header mb-3">
                  <v-icon small class="mr-1">mdi-bus</v-icon>
                  Bus
                </div>
                <v-layout row wrap>
                  <v-flex xs12 sm6 class="pr-2">
                    <v-autocomplete
                      v-model="selectedPickupRoute"
                      :items="filteredPickupRoutes"
                      :filter="customRouteFilter"
                      item-text="description"
                      item-value="id"
                      label="Pickup Route"
                      prepend-inner-icon="mdi-bus-clock"
                      outlined
                      dense
                      clearable
                      :loading="loadingBusRoutes"
                      :disabled="isViewOnly"
                      placeholder="Start typing to search..."
                      return-object
                      @change="onPickupRouteChange"
                      class="route-autocomplete"
                      :menu-props="{ bottom: true, offsetY: true }"
                    >
                      <template v-slot:selection="{ item }">
                        <div class="route-selection-wrapper">
                          <div class="route-selection-main">
                            {{ item.description }}
                            <span
                              v-if="getRouteBusNumber(item)"
                              class="route-detail-chip"
                              :style="getRouteChipStyle(item)"
                            >
                              Bus #{{ getRouteBusNumber(item) }}
                            </span>
                          </div>
                          <div
                            v-if="item.driver"
                            class="route-selection-driver"
                          >
                            <v-icon x-small>mdi-account</v-icon>
                            {{ item.driver }}
                          </div>
                        </div>
                      </template>
                      <template v-slot:item="{ item }">
                        <v-list-tile-content
                          class="route-list-item"
                          :style="
                            getRouteColor(item.color)
                              ? `border-left: 4px solid ${getRouteColor(
                                  item.color
                                )}`
                              : ''
                          "
                        >
                          <v-list-tile-title>
                            <strong>{{ item.description }}</strong>
                          </v-list-tile-title>
                          <v-list-tile-sub-title>
                            <span v-if="getRouteBusNumber(item)" class="mr-2">
                              <v-icon x-small color="grey darken-1">mdi-bus</v-icon>
                              Bus #{{ getRouteBusNumber(item) }}
                            </span>
                            <span v-if="item.driver" class="mr-2">
                              <v-icon x-small color="grey darken-1"
                                >mdi-account</v-icon
                              >
                              {{ item.driver }}
                            </span>
                            <span v-if="item.color" class="mr-2">
                              <v-icon
                                x-small
                                :style="
                                  getRouteColor(item.color)
                                    ? `color: ${getRouteColor(item.color)}`
                                    : ''
                                "
                                >mdi-circle</v-icon
                              >
                              {{ item.color }}
                            </span>
                          </v-list-tile-sub-title>
                        </v-list-tile-content>
                      </template>
                    </v-autocomplete>
                    <div v-if="selectedPickupRoute" class="geo-stop-picker mt-2">
                      <v-autocomplete
                        v-model="selectedPickupStopNumber"
                        :items="pickupRouteStops"
                        item-text="label"
                        item-value="stopNumber"
                        label="Pickup Geo Stop"
                        prepend-inner-icon="mdi-map-marker-radius"
                        outlined
                        dense
                        clearable
                        :loading="loadingPickupStops"
                        :disabled="isViewOnly || loadingPickupStops"
                        placeholder="Select stop on this bus..."
                        :menu-props="{ bottom: true, offsetY: true, maxHeight: 320 }"
                        :error="!!selectedPickupRoute && !selectedPickupStopNumber"
                        :hint="
                          selectedPickupStopNumber
                            ? 'Used for GeoTrack'
                            : 'Required for GeoTrack sync'
                        "
                        persistent-hint
                        @change="onPickupStopNumberChange"
                      >
                        <template v-slot:selection="{ item }">
                          <span class="geo-stop-selection">
                            <span class="geo-stop-badge">{{ item.stopNumber }}</span>
                            <span class="geo-stop-address">{{
                              item.stopAddress || item.label
                            }}</span>
                          </span>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-list-tile-content>
                            <v-list-tile-title class="geo-stop-item-title">
                              <span class="geo-stop-badge">{{
                                item.stopNumber
                              }}</span>
                              {{ item.stopAddress }}
                            </v-list-tile-title>
                          </v-list-tile-content>
                        </template>
                      </v-autocomplete>
                    </div>
                  </v-flex>
                  <v-flex xs12 sm6 class="pl-2">
                    <v-autocomplete
                      v-model="selectedDropoffRoute"
                      :items="filteredDropoffRoutes"
                      :filter="customRouteFilter"
                      item-text="description"
                      item-value="id"
                      label="Dropoff Route"
                      prepend-inner-icon="mdi-bus-clock"
                      outlined
                      dense
                      clearable
                      :loading="loadingBusRoutes"
                      :disabled="isViewOnly"
                      placeholder="Start typing to search..."
                      return-object
                      @change="onDropoffRouteChange"
                      class="route-autocomplete"
                      :menu-props="{ bottom: true, offsetY: true }"
                    >
                      <template v-slot:selection="{ item }">
                        <div class="route-selection-wrapper">
                          <div class="route-selection-main">
                            {{ item.description }}
                            <span
                              v-if="getRouteBusNumber(item)"
                              class="route-detail-chip"
                              :style="getRouteChipStyle(item)"
                            >
                              Bus #{{ getRouteBusNumber(item) }}
                            </span>
                          </div>
                          <div
                            v-if="item.driver"
                            class="route-selection-driver"
                          >
                            <v-icon x-small>mdi-account</v-icon>
                            {{ item.driver }}
                          </div>
                        </div>
                      </template>
                      <template v-slot:item="{ item }">
                        <v-list-tile-content
                          class="route-list-item"
                          :style="
                            getRouteColor(item.color)
                              ? `border-left: 4px solid ${getRouteColor(
                                  item.color
                                )}`
                              : ''
                          "
                        >
                          <v-list-tile-title>
                            <strong>{{ item.description }}</strong>
                          </v-list-tile-title>
                          <v-list-tile-sub-title>
                            <span v-if="getRouteBusNumber(item)" class="mr-2">
                              <v-icon x-small color="grey darken-1">mdi-bus</v-icon>
                              Bus #{{ getRouteBusNumber(item) }}
                            </span>
                            <span v-if="item.driver" class="mr-2">
                              <v-icon x-small color="grey darken-1"
                                >mdi-account</v-icon
                              >
                              {{ item.driver }}
                            </span>
                            <span v-if="item.color" class="mr-2">
                              <v-icon
                                x-small
                                :style="
                                  getRouteColor(item.color)
                                    ? `color: ${getRouteColor(item.color)}`
                                    : ''
                                "
                                >mdi-circle</v-icon
                              >
                              {{ item.color }}
                            </span>
                          </v-list-tile-sub-title>
                        </v-list-tile-content>
                      </template>
                    </v-autocomplete>
                    <div v-if="selectedDropoffRoute" class="geo-stop-picker mt-2">
                      <v-autocomplete
                        v-model="selectedDropoffStopNumber"
                        :items="dropoffRouteStops"
                        item-text="label"
                        item-value="stopNumber"
                        label="Dropoff Geo Stop"
                        prepend-inner-icon="mdi-map-marker-radius"
                        outlined
                        dense
                        clearable
                        :loading="loadingDropoffStops"
                        :disabled="isViewOnly || loadingDropoffStops"
                        placeholder="Select stop on this bus..."
                        :menu-props="{ bottom: true, offsetY: true, maxHeight: 320 }"
                        :error="!!selectedDropoffRoute && !selectedDropoffStopNumber"
                        :hint="
                          selectedDropoffStopNumber
                            ? 'Used for GeoTrack'
                            : 'Required for GeoTrack sync'
                        "
                        persistent-hint
                      >
                        <template v-slot:selection="{ item }">
                          <span class="geo-stop-selection">
                            <span class="geo-stop-badge">{{ item.stopNumber }}</span>
                            <span class="geo-stop-address">{{
                              item.stopAddress || item.label
                            }}</span>
                          </span>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-list-tile-content>
                            <v-list-tile-title class="geo-stop-item-title">
                              <span class="geo-stop-badge">{{
                                item.stopNumber
                              }}</span>
                              {{ item.stopAddress }}
                            </v-list-tile-title>
                          </v-list-tile-content>
                        </template>
                      </v-autocomplete>
                    </div>
                  </v-flex>
                </v-layout>
              </v-card>
            </v-flex>

            <v-flex xs12 md3 class="pl-2">
              <div
                v-if="hasPendingLinkedRequest()"
                class="pending-request-indicator mb-2"
              >
                Last request is awaiting action.
              </div>
              <v-card outlined class="pa-2 comments-card">
                <div class="section-header mb-2">
                  <v-icon x-small class="mr-1">mdi-message-text</v-icon>
                  Messages
                </div>
                <div
                  v-if="loadingBusChangeComments"
                  class="d-flex align-center grey--text text--darken-1"
                >
                  <v-progress-circular
                    size="18"
                    width="2"
                    indeterminate
                    color="success"
                    class="mr-2"
                  ></v-progress-circular>
                  Loading...
                </div>
                <div
                  v-else-if="busChangeComments.length === 0"
                  class="grey--text text--darken-1"
                >
                  No messages.
                </div>
                <div v-else>
                  <div
                    v-for="(comment, index) in busChangeComments"
                    :key="comment.id || comment.ID || comment.callUUID || index"
                    class="comment-entry"
                  >
                    <div class="comment-meta">
                      <span class="font-weight-medium">
                        {{ getCommentEnteredBy(comment) || "Parent" }}
                      </span>
                      <span class="mx-1">-</span>
                      <span>{{
                        formatDateTime(getCommentEnteredOn(comment))
                      }}</span>
                    </div>
                    <div
                      v-if="
                        isCommentAwaitingResponse(
                          comment,
                          index,
                          busChangeComments
                        )
                      "
                      class="comment-awaiting mt-1"
                    >
                      Action required
                    </div>
                    <div
                      v-if="getCommentCallerID(comment)"
                      class="comment-caller grey--text text--darken-1"
                    >
                      <v-icon x-small class="mr-1">mdi-phone</v-icon>
                      {{ formatPhoneNumber(getCommentCallerID(comment)) }}
                    </div>
                    <div
                      v-if="getCommentText(comment)"
                      class="comment-text grey--text text--darken-3"
                    >
                      {{ getCommentText(comment) }}
                    </div>
                    <audio
                      v-if="getCommentRecordingUrl(comment)"
                      controls
                      controlsList="nodownload"
                      preload="metadata"
                      :src="getCommentRecordingUrl(comment)"
                      class="comment-audio mt-2"
                    ></audio>
                  </div>
                </div>
              </v-card>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-divider class="py-0"></v-divider>

        <v-card-actions class="py-3 pr-3">
          <v-btn
            v-if="!isViewOnly"
            color="error"
            dark
            :loading="deleting"
            :disabled="saving"
            @click="openDeleteDialog"
          >
            <v-icon left>mdi-delete</v-icon>
            Delete
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" dark @click="close">
            Close
          </v-btn>
          <v-btn
            v-if="!isViewOnly"
            color="success"
            dark
            :loading="saving"
            :disabled="deleting"
            @click="save"
            class="save-btn"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="560">
      <v-card>
        <v-card-title class="headline error white--text">
          <v-icon left dark>mdi-alert-circle</v-icon>
          Delete Bus Change
        </v-card-title>
        <v-card-text class="pt-3 pb-2">
          <v-alert
            type="warning"
            outline
            value="true"
            class="delete-warning-banner mb-3"
          >
            <strong>Permanent action:</strong> deleting this bus change cannot
            be undone.
          </v-alert>
          <div v-if="busChange" class="delete-summary-card">
            <div class="delete-summary-title">
              <v-icon small color="error" class="mr-1">mdi-bus-clock</v-icon>
              Bus Change Details
            </div>
            <div class="delete-summary-grid">
              <div class="delete-summary-item">
                <div class="delete-label">Student</div>
                <div class="delete-value">
                  {{ busChange.studentName || "-" }}
                  <span v-if="busChange.studentID"
                    >({{ busChange.studentID }})</span
                  >
                </div>
              </div>
              <div class="delete-summary-item">
                <div class="delete-label">Branch / Class</div>
                <div class="delete-value">
                  {{ busChange.branch || "-" }} /
                  {{ busChange.class || "-" }}-{{ busChange.section || "-" }}
                </div>
              </div>
              <div class="delete-summary-item">
                <div class="delete-label">Date Range</div>
                <div class="delete-value">
                  {{ formData.startDate || "-" }} to
                  {{ formData.endDate || "Open" }}
                </div>
              </div>
              <div class="delete-summary-item">
                <div class="delete-label">Run</div>
                <div class="delete-value">
                  {{ formatPeriodLabel(formData.startPeriod) }} to
                  {{ formatPeriodLabel(formData.endPeriod) }}
                </div>
              </div>
              <div class="delete-summary-item delete-summary-item--full">
                <div class="delete-label">Address</div>
                <div class="delete-value">
                  {{ formData.busChangeAddress || "-" }}
                </div>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" dark :loading="deleting" @click="confirmDelete">
            <v-icon left>mdi-delete</v-icon>
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Outside Day Camp Enrollment Confirmation Dialog -->
    <v-dialog v-model="enrollmentWarningDialog" max-width="520">
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-tent</v-icon>
          Outside Day Camp Enrollment
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outline value="true" class="mb-0 pa-2">
            {{ enrollmentDateWarning }}
          </v-alert>
          <div class="mt-3">Do you want to continue saving anyway?</div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="cancelEnrollmentWarning">
            Cancel
          </v-btn>
          <v-btn color="warning" dark @click="confirmSaveWithEnrollmentWarning">
            Continue Saving
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Incomplete Route Confirmation Dialog -->
    <v-dialog v-model="incompleteRouteDialog" max-width="500">
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-alert</v-icon>
          Incomplete Route Selection
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outline value="true" class="mb-0 pa-2">
            {{ getIncompleteRouteMessage() }}
          </v-alert>
          <div class="mt-3">Do you want to continue saving anyway?</div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="incompleteRouteDialog = false">
            Cancel
          </v-btn>
          <v-btn color="warning" dark @click="confirmSaveWithIncompleteRoute">
            Continue Saving
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Missing Geo Stop Confirmation Dialog -->
    <v-dialog v-model="missingGeoStopDialog" max-width="520">
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-map-marker-off</v-icon>
          Geo Stop Not Selected
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outline value="true" class="mb-0 pa-2">
            {{ missingGeoStopMessage }}
          </v-alert>
          <div class="mt-3">
            GeoTrack will not sync for routes without a stop. Continue saving
            anyway?
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="missingGeoStopDialog = false">
            Go Back
          </v-btn>
          <v-btn color="warning" dark @click="confirmSaveWithoutGeoStop">
            Continue Without Geo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Notification Confirmation Dialog -->
    <v-dialog v-model="notificationConfirmDialog" max-width="550" persistent>
      <v-card>
        <v-card-title class="headline success white--text">
          <v-icon left dark>mdi-information</v-icon>
          Notification Will Be Sent
        </v-card-title>
        <v-card-text class="pt-3">
          <v-alert value="true" type="info" outline class="mb-3">
            <div class="font-weight-bold mb-1">
              <v-icon small color="info" class="mr-1">
                {{
                  formData.notificationType === "CALL"
                    ? "mdi-phone"
                    : "mdi-message-text"
                }}
              </v-icon>
              {{
                formData.notificationType === "CALL"
                  ? "A phone call"
                  : "A text message"
              }}
              will automatically be sent to:
            </div>
            <div class="ml-4">
              {{ formatPhoneNumber(formData.notificationPhoneNumber) }}
            </div>
          </v-alert>
          <div class="grey--text text--darken-1">
            The bus change will be saved and the notification will be sent
            automatically.
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-btn color="default" dark @click="cancelSave">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="success" dark @click="proceedWithNotification">
            <v-icon left small>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Missing Notification Fields Dialog -->
    <v-dialog
      v-model="missingNotificationFieldsDialog"
      max-width="550"
      persistent
    >
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-alert-circle</v-icon>
          Missing Notification Information
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outlined class="mb-3">
            <div class="font-weight-bold mb-2">
              Cannot send notification - missing required fields:
            </div>
            <ul class="ml-4">
              <li v-if="!formData.notificationPhoneNumber">
                Notification Phone Number
              </li>
              <li v-if="!formData.notificationType">Notification Type</li>
            </ul>
          </v-alert>
          <div class="mt-3">
            Would you like to fill in these fields or save without sending a
            notification?
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-btn color="default" dark @click="cancelSave">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="warning" @click="saveWithoutNotification">
            Save Without Notification
          </v-btn>
          <v-btn color="success" dark @click="closeMissingFieldsDialog">
            Fill in Fields
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cleared Notification Warning Dialog (IVR) -->
    <v-dialog
      v-model="clearedNotificationWarningDialog"
      max-width="550"
      persistent
    >
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-alert</v-icon>
          Notification Fields Cleared
        </v-card-title>
        <v-card-text class="pt-3">
          <v-alert type="warning" outline value="true" outlined class="mb-3">
            <div class="font-weight-bold mb-2 grey--text text--darken-4">
              This record originally had notification information
            </div>
            <div class="grey--text text--darken-3">
              By clearing the notification fields, the user will NOT receive a
              notification about this bus change.
            </div>
          </v-alert>
          <div class="mt-3">
            Do you want to continue saving without notification?
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="cancelClearedNotificationWarning">
            Cancel
          </v-btn>
          <v-btn color="warning" dark @click="confirmSaveWithoutNotification">
            Save Without Notification
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Account Search Dialog -->
    <v-dialog v-model="accountSearchDialog" max-width="800">
      <v-card>
        <v-card-title class="dialog-header">
          <v-icon class="mr-3" color="white" size="28"
            >mdi-account-search</v-icon
          >
          <span class="dialog-title">Search Account</span>
          <v-spacer></v-spacer>
          <v-btn
            class="white--text"
            icon
            dark
            @click="closeAccountSearchDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Phone Number Search -->
          <v-layout row wrap class="mb-1">
            <v-flex xs12 class="pr-2">
              <v-text-field
                v-model="dialogPhoneSearch"
                label="Search by Phone Number"
                prepend-inner-icon="mdi-phone"
                outlined
                dense
                clearable
                mask="###-###-####"
                @input="searchDialogByPhone"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Search Name Mode Toggle -->
          <v-layout row wrap align-center class="mb-2">
            <v-flex xs12 class="pr-2">
              <div class="caption grey--text mb-1">Or Search By Name</div>
              <v-btn-toggle
                v-model="dialogSearchNameMode"
                mandatory
                class="search-toggle"
              >
                <v-btn flat small value="yiddish">אידיש</v-btn>
                <v-btn flat small value="english">English</v-btn>
              </v-btn-toggle>
            </v-flex>
          </v-layout>

          <!-- Account Search Fields -->
          <v-layout row wrap class="mb-3">
            <v-flex xs12 sm6 class="pr-2">
              <v-text-field
                v-model="dialogLastNameSearch"
                :label="
                  dialogSearchNameMode === 'yiddish'
                    ? 'לעצטע נאמען'
                    : 'Last Name'
                "
                prepend-inner-icon="mdi-account"
                outlined
                dense
                clearable
                @input="searchDialogByName"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6>
              <v-text-field
                v-model="dialogFirstNameSearch"
                :label="
                  dialogSearchNameMode === 'yiddish'
                    ? 'טאטעס נאמען'
                    : 'Father Name'
                "
                prepend-inner-icon="mdi-account"
                outlined
                dense
                clearable
                @input="searchDialogByName"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Address Search -->
          <v-layout row wrap class="mb-2">
            <v-flex xs12>
              <div class="caption grey--text mb-1">Or Search By Address</div>
            </v-flex>
          </v-layout>
          <v-layout row wrap class="mb-3">
            <v-flex xs12 sm6 class="pr-2">
              <v-text-field
                v-model="dialogHouseNumberSearch"
                label="House Number"
                prepend-inner-icon="mdi-home"
                outlined
                dense
                clearable
                @input="searchDialogByAddress"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6>
              <v-text-field
                v-model="dialogStreetSearch"
                label="Street Name"
                prepend-inner-icon="mdi-road"
                outlined
                dense
                clearable
                @input="searchDialogByAddress"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Account List -->
          <div class="account-list-container" v-if="dialogAccounts.length > 0">
            <div class="caption grey--text mb-2 px-2">
              Select an account ({{ dialogAccounts.length }} found)
            </div>
            <v-list dense class="account-list">
              <v-list-tile
                v-for="account in dialogAccounts"
                :key="account.accountID"
                @click="onAccountSelected(account)"
                class="account-item"
              >
                <v-list-tile-avatar>
                  <v-icon color="rgb(92, 184, 96)">mdi-account-box</v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content>
                  <v-list-tile-title>
                    <strong>{{ account.name }}</strong>
                    <span class="grey--text ml-2">({{ account.hname }})</span>
                  </v-list-tile-title>
                  <v-list-tile-sub-title>
                    Account ID: {{ account.accountID }}
                    <span v-if="account.homePhone" class="ml-1">
                      | {{ account.homePhone }}
                    </span>
                    <span v-if="account.cellPhone" class="ml-1">
                      | {{ account.cellPhone }}
                    </span>
                    <span v-if="account.address1" class="ml-1">
                      | {{ account.address1 }}
                    </span>
                    <span v-if="account.busAddress1" class="ml-1">
                      | Bus: {{ account.busAddress1 }}
                    </span>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
                <v-list-tile-action>
                  <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
                </v-list-tile-action>
              </v-list-tile>
            </v-list>
          </div>

          <!-- No Accounts Message -->
          <v-alert
            v-if="dialogShowNoAccountsMessage && dialogAccounts.length === 0"
            type="warning"
            outline
            dense
            value="true"
            class="mb-3"
          >
            No accounts found. Please try a different search.
          </v-alert>

          <!-- Empty state -->
          <div
            v-if="!dialogShowNoAccountsMessage && dialogAccounts.length === 0"
            class="text-center px-2 pb-3 grey--text"
          >
            <v-icon large color="grey lighten-1">mdi-account-search</v-icon>
            <div class="mt-2">
              Search for an account by phone, name, or address
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import { normalizeRecordingUrl } from "../../utils/helpers.js";
import { getBusChangeNotificationNotices } from "./busChangeNotification.js";

export default {
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    busChangeId: {
      type: Number,
      default: null,
    },
    isViewOnly: {
      type: Boolean,
      default: false,
    },
    sendNotificationOnSave: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    internalDialog: false,
    busChange: null,
    linkedRequest: null,
    loading: false,
    saving: false,
    editingExtraInfo: false,
    savingExtraInfo: false,
    extraInfoBackup: null,
    deleteDialog: false,
    deleting: false,
    busChangeComments: [],
    loadingBusChangeComments: false,

    editingEndInfo: false,
    savingEndInfo: false,
    endEditReturnRoute: null,
    endEditReturnStopNumber: null,
    endEditReturnRouteStops: [],
    loadingEndReturnStops: false,

    rules: {
      required: (value) => !!value || "Required.",
    },

    // Date pickers
    startDateMenu: false,
    endDateMenu: false,

    // Bus routes
    busRoutes: [],
    filteredPickupRoutes: [],
    filteredDropoffRoutes: [],
    selectedPickupRoute: null,
    selectedDropoffRoute: null,
    loadingBusRoutes: false,
    summerDates: null,
    dayCampEnrollment: null,
    enrollmentWarningDialog: false,
    skipEnrollmentWarning: false,
    selectedGradeDeadlines: [],
    busChangeGrades: [],

    // Bus change notification dialogs and state
    notificationConfirmDialog: false,
    missingNotificationFieldsDialog: false,
    clearedNotificationWarningDialog: false,
    incompleteRouteDialog: false,
    skipRouteWarning: false,
    missingGeoStopDialog: false,
    skipGeoStopWarning: false,
    missingGeoStopMessage: "",
    selectedPickupStopNumber: null,
    selectedDropoffStopNumber: null,
    pickupRouteStops: [],
    dropoffRouteStops: [],
    loadingPickupStops: false,
    loadingDropoffStops: false,
    skipNotification: false,
    originalPickupRouteID: null,
    originalDropoffRouteID: null,
    originalNotificationPhone: null,
    originalNotificationType: null,
    savedBusChangeId: null,
    sendingNotification: false,

    // Account search dialog
    accountSearchDialog: false,
    dialogPhoneSearch: "",
    dialogFirstNameSearch: "",
    dialogLastNameSearch: "",
    dialogHouseNumberSearch: "",
    dialogStreetSearch: "",
    dialogAccounts: [],
    dialogSearchNameMode: "yiddish",
    dialogShowNoAccountsMessage: false,
    dialogSearchDebounceTimer: null,

    // Form data
    formData: {
      startDate: "",
      endDate: "",
      startPeriod: "",
      endPeriod: "",
      busChangeAddress: "",
      busChangePhoneNumber: "",
      geoPhoneNumber: "",
      busChangeName: "",
      busChangeAccountID: "",
      notificationPhoneNumber: "",
      notificationType: "",
      note: "",
    },
    periodOptions: [
      { label: "Morning Pickup", value: "AM" },
      { label: "Afternoon Dropoff", value: "PM" },
    ],
  }),

  computed: {
    dialogTitle() {
      const base = this.isViewOnly ? "View Bus Change" : "Edit Bus Change";
      if (!this.isBusChangeEnded) return base;
      return base + (this.isEndDateToday ? " (Ends Today)" : " (Ended)");
    },
    isBusChangeEnded() {
      return !!(
        this.busChange &&
        (this.busChange.finished === true || this.busChange.Finished === true)
      );
    },
    // The end date is a calendar date, not a moment in time — while it's
    // still that day the change hasn't fully "ended" yet, it's ending today.
    isEndDateToday() {
      if (!this.busChange || !this.busChange.endDate) return false;
      const end = this.busChange.endDate || this.busChange.EndDate;
      if (!end) return false;
      const endKey =
        typeof end === "string" && /^\d{4}-\d{2}-\d{2}/.test(end)
          ? end.slice(0, 10)
          : moment(end).format("YYYY-MM-DD");
      return endKey === moment().format("YYYY-MM-DD");
    },
    endedBannerTitle() {
      return this.isEndDateToday ? "Bus Change Ends Today" : "Bus Change Ended";
    },
    infoFamilyId() {
      if (!this.busChange) return "";
      const value =
        this.busChange.familyID || this.busChange.FamilyID || "";
      return String(value).trim();
    },
    endedReturnRoute() {
      if (!this.busChange) return null;
      const id = this.busChange.returnRouteID || this.busChange.ReturnRouteID;
      if (!id) return null;
      return (this.busRoutes || []).find((r) => r.id === id) || null;
    },
    endedReturnStopNumber() {
      if (!this.busChange) return "";
      const value =
        this.busChange.returnStopNumber != null
          ? this.busChange.returnStopNumber
          : this.busChange.ReturnStopNumber;
      return value != null ? String(value).trim() : "";
    },
    studentOriginalBusAddress() {
      if (!this.busChange) return "";
      const value =
        this.busChange.studentBusAddress1 || this.busChange.StudentBusAddress1;
      return (value || "").toString().trim();
    },
    userBranch() {
      const branch = this.$store.state.authentication.branch;
      if (branch && !branch.includes("null")) {
        return branch;
      }
      return null;
    },
    deadlineWarningMessage() {
      if (!this.busChange) return null;
      const ctx = this.getDeadlineGradeContext();
      return this.checkDeadlineWarning(
        ctx.grade,
        ctx.scope,
        this.formData.startDate
      );
    },
    isDayCampView() {
      if (!this.summerDates || !this.busChange) return false;
      const bcStart = this.busChange.startDate
        ? moment(this.busChange.startDate).startOf("day")
        : null;
      if (!bcStart || !bcStart.isValid()) return false;
      const bcEnd =
        this.busChange.endDate && moment(this.busChange.endDate).isValid()
          ? moment(this.busChange.endDate).startOf("day")
          : bcStart.clone();
      const seasonStart = moment(this.summerDates.startDate).startOf("day");
      const seasonEnd = moment(this.summerDates.endDate).startOf("day");
      return (
        bcStart.isSameOrBefore(seasonEnd) && bcEnd.isSameOrAfter(seasonStart)
      );
    },
    summerSeasonName() {
      const year =
        this.summerDates && this.summerDates.startDate
          ? moment(this.summerDates.startDate).year()
          : moment().year();
      return `Summer ${year}`;
    },
    viewBuilding() {
      if (
        this.isDayCampView &&
        this.dayCampEnrollment &&
        this.dayCampEnrollment.building
      ) {
        return this.dayCampEnrollment.building;
      }
      return this.busChange ? this.busChange.building : "";
    },
    viewClass() {
      if (
        this.isDayCampView &&
        this.dayCampEnrollment &&
        this.dayCampEnrollment.class
      ) {
        return this.dayCampEnrollment.class;
      }
      return this.busChange ? this.busChange.class : "";
    },
    viewSection() {
      if (
        this.isDayCampView &&
        this.dayCampEnrollment &&
        this.dayCampEnrollment.section
      ) {
        return this.dayCampEnrollment.section;
      }
      return this.busChange ? this.busChange.section : "";
    },
    viewTeacher() {
      // In day camp view prefer the day camp teacher (may be empty so Room shows).
      if (this.isDayCampView && this.dayCampEnrollment) {
        return this.getDcTeacher(this.dayCampEnrollment);
      }
      if (!this.busChange) return "";
      const teachers = [];
      if (this.busChange.teacherH) teachers.push(this.busChange.teacherH);
      if (this.busChange.teacherH2) teachers.push(this.busChange.teacherH2);
      return teachers.join(" / ");
    },
    enrollmentDateWarning() {
      return this.getEnrollmentDateWarning();
    },
    linkedRequestCallerID() {
      if (!this.linkedRequest) return "";
      return this.linkedRequest.callerID || this.linkedRequest.CallerID || "";
    },
    playableRecordingUrl() {
      const fromBusChange =
        this.busChange &&
        (this.busChange.recordingURL ||
          this.busChange.RecordingURL ||
          this.busChange.recordingUrl);
      const fromLinked =
        this.linkedRequest &&
        (this.linkedRequest.recordingURL ||
          this.linkedRequest.RecordingURL ||
          this.linkedRequest.recordingUrl);
      return normalizeRecordingUrl(fromBusChange || fromLinked || null);
    },
  },

  watch: {
    dialog(val) {
      this.internalDialog = val;
      if (val && this.busChangeId) {
        this.loadBusChange();
      }
    },
    internalDialog(val) {
      if (!val) {
        this.$emit("update:dialog", false);
        this.$emit("close");
      }
    },
    dialogSearchNameMode() {
      // Re-search when switching between English and Yiddish
      if (this.dialogFirstNameSearch || this.dialogLastNameSearch) {
        this.searchDialogByName();
      }
    },
  },

  methods: {
    async loadBusChange() {
      this.loading = true;
      try {
        // Load bus change data
        const busChange = await this.$http.get(
          `api/BusChange/GetById?id=${this.busChangeId}`
        );

        if (!busChange) {
          this.$notify({
            text: "Bus change not found",
            group: "notification",
            type: "error",
          });
          this.close();
          return;
        }

        this.busChange = busChange;
        await this.loadSummerDates();
        // Load day camp enrollment info when this bus change is in the season.
        await this.loadDayCampInfoForView();
        await this.loadBusChangeGrades();
        // Day Camp season → grade + building; School → grade + branch
        const deadlineCtx = this.getDeadlineGradeContext();
        await this.loadSelectedGradeDeadlines(
          deadlineCtx.grade,
          deadlineCtx.scope,
          deadlineCtx.isDayCamp
        );
        await this.loadLinkedRequestForBusChange(busChange.id || this.busChangeId);
        await this.loadBusChangeComments(
          this.getBusChangeRequestIdFromBusChange(busChange)
        );

        // Set form data
        this.formData = {
          startDate: busChange.startDate
            ? this.formatDateForPicker(busChange.startDate)
            : "",
          endDate: busChange.endDate
            ? this.formatDateForPicker(busChange.endDate)
            : "",
          startPeriod: busChange.startPeriod || busChange.StartPeriod || "",
          endPeriod: busChange.endPeriod || busChange.EndPeriod || "",
          busChangeAddress: busChange.busChangeAddress || "",
          busChangePhoneNumber: busChange.busChangePhoneNumber || "",
          geoPhoneNumber: busChange.geoPhoneNumber || "",
          busChangeName: busChange.busChangeName || "",
          busChangeAccountID: busChange.busChangeAccountID || "",
          notificationPhoneNumber: busChange.notificationPhoneNumber || "",
          notificationType: busChange.notificationType || "",
          note: busChange.note || "",
        };

        // Load bus routes
        await this.loadBusRoutes();

        // Set selected routes
        const pickupStopNumber =
          busChange.pickupStopNumber != null
            ? busChange.pickupStopNumber
            : null;
        const dropoffStopNumber =
          busChange.dropoffStopNumber != null
            ? busChange.dropoffStopNumber
            : null;

        if (busChange.pickupRouteID) {
          const pickupRoute = this.busRoutes.find(
            (r) => r.id === busChange.pickupRouteID
          );
          this.selectedPickupRoute = pickupRoute || null;
          if (pickupRoute) {
            await this.loadRouteStops(
              "pickup",
              pickupRoute.id,
              pickupStopNumber
            );
          }
        }
        if (busChange.dropoffRouteID) {
          const dropoffRoute = this.busRoutes.find(
            (r) => r.id === busChange.dropoffRouteID
          );
          this.selectedDropoffRoute = dropoffRoute || null;
          if (dropoffRoute) {
            await this.loadRouteStops(
              "dropoff",
              dropoffRoute.id,
              dropoffStopNumber
            );
          }
        }

        // Track original route IDs for notification detection
        this.originalPickupRouteID = busChange.pickupRouteID || null;
        this.originalDropoffRouteID = busChange.dropoffRouteID || null;

        // Track original notification fields for IVR validation
        this.originalNotificationPhone =
          busChange.notificationPhoneNumber || null;
        this.originalNotificationType = busChange.notificationType || null;
      } catch (e) {
        this.close();
      } finally {
        this.loading = false;
      }
    },

    async loadSummerDates() {
      if (this.summerDates) return;
      try {
        const response = await this.$http.get("api/daycamp/getSummerDates");
        if (response && response.startDate && response.endDate) {
          this.summerDates = {
            startDate: response.startDate,
            endDate: response.endDate,
          };
        }
      } catch (e) {
        this.summerDates = null;
      }
    },

    filterRoutesBySeason(routes) {
      // In day camp mode, only show summer / DayCamp routes — never year-round.
      if (!this.isDayCampView) return routes;
      if (!routes.some((r) => r.season || r.kind)) return routes;
      const target = this.summerSeasonName.trim().toLowerCase();
      return routes.filter((r) => {
        const kind = (r.kind || "").trim().toLowerCase();
        if (kind === "daycamp") return true;
        const season = (r.season || "").trim().toLowerCase();
        if (!season) return true;
        return season === target;
      });
    },

    async loadBusRoutes() {
      this.loadingBusRoutes = true;
      try {
        // For day camp bus changes, ask the backend for only the summer season's routes.
        let url = "api/buschange/GetBusRoutes";
        if (this.isDayCampView) {
          url +=
            `?season=${encodeURIComponent(this.summerSeasonName)}` +
            "&kind=DayCamp";
        }
        this.busRoutes = await this.$http.get(url);

        // Filter routes by building if available. In day camp view prefer the
        // student's day camp building so routes match the day camp location.
        const building =
          (this.isDayCampView &&
            this.dayCampEnrollment &&
            this.dayCampEnrollment.building) ||
          (this.busChange && this.busChange.building);
        if (building) {
          this.filteredPickupRoutes = this.filterRoutesBySeason(
            this.busRoutes.filter((r) => {
              if (r.direction !== "Pickup") return false;
              if (!r.buildings) return true;
              const routeBuildings = r.buildings
                .split(",")
                .map((b) => b.trim().toUpperCase());
              return routeBuildings.includes(building.toString().toUpperCase());
            })
          );

          this.filteredDropoffRoutes = this.filterRoutesBySeason(
            this.busRoutes.filter((r) => {
              if (r.direction !== "Dropoff") return false;
              if (!r.buildings) return true;
              const routeBuildings = r.buildings
                .split(",")
                .map((b) => b.trim().toUpperCase());
              return routeBuildings.includes(building.toString().toUpperCase());
            })
          );
        } else {
          this.filteredPickupRoutes = this.filterRoutesBySeason(
            this.busRoutes.filter((r) => r.direction === "Pickup")
          );
          this.filteredDropoffRoutes = this.filterRoutesBySeason(
            this.busRoutes.filter((r) => r.direction === "Dropoff")
          );
        }
      } catch (e) {
        this.busRoutes = [];
        this.filteredPickupRoutes = [];
        this.filteredDropoffRoutes = [];
      } finally {
        this.loadingBusRoutes = false;
      }
    },

    customRouteFilter(item, queryText, itemText) {
      if (!queryText) return true;
      const search = String(queryText).toUpperCase().trim();
      const route = item && typeof item === "object" ? item : null;
      if (!route) {
        return String(itemText || item || "")
          .toUpperCase()
          .includes(search);
      }
      const fields = [
        route.description,
        route.busNumber,
        route.actualBusNumber,
        route.ActualBusNumber,
        route.BusNumber,
        route.driver,
        route.color,
        route.timeStart,
        route.routeIdentifier,
        itemText,
      ];
      return fields.some(
        (f) => f != null && String(f).toUpperCase().includes(search)
      );
    },

    getRouteBusNumber(route) {
      // Prefer actualBusNumber from GetBusRoutes when present; else busNumber
      if (!route) return "";
      const actual = (route.actualBusNumber || "").toString().trim();
      if (actual) return actual;
      return (route.busNumber || "").toString().trim();
    },

    getRouteColor(colorName) {
      if (!colorName) return null;

      const colorUpper = colorName.toUpperCase().trim();
      const colorMap = {
        BEIGE: "#F5F5DC",
        BLACK: "#000000",
        BLUE: "#0000FF",
        BROWN: "#8B4513",
        GOLD: "#FFD700",
        GREEN: "#008000",
        ORANGE: "#FFA500",
        PINK: "#FFC0CB",
        PURPLE: "#800080",
        RED: "#FF0000",
        SILVER: "#C0C0C0",
        WHITE: "#E0E0E0",
        YELLOW: "#FFFF00",
        "DARK BLUE": "#00008B",
        "DARK GREEN": "#006400",
        "DARK PINK": "#FF1493",
        "DARK PURPLE": "#4B0082",
        "LIGHT BLUE": "#ADD8E6",
        "LIGHT GREEN": "#90EE90",
        "LIGHT PINK": "#FFB6C1",
        "LIGHT PURPLE": "#DDA0DD",
        GRAY: "#808080",
        GREY: "#808080",
        CYAN: "#00FFFF",
        TEAL: "#008080",
        LIME: "#00FF00",
        INDIGO: "#4B0082",
        NAVY: "#000080",
        MAROON: "#800000",
        OLIVE: "#808000",
        VIOLET: "#EE82EE",
        TURQUOISE: "#40E0D0",
        CORAL: "#FF7F50",
        SALMON: "#FA8072",
        MINT: "#98FF98",
        LAVENDER: "#E6E6FA",
        PEACH: "#FFE5B4",
        SKY: "#87CEEB",
        ROSE: "#FF007F",
      };

      return colorMap[colorUpper] || "#5cb860";
    },

    getRouteChipStyle(route) {
      if (!route || !route.color) {
        return {
          background: "#5cb860",
          color: "#FFFFFF",
          border: "1px solid rgba(0, 0, 0, 0.2)",
        };
      }

      const bgColor = this.getRouteColor(route.color);
      if (!bgColor) {
        return {
          background: "#5cb860",
          color: "#FFFFFF",
          border: "1px solid rgba(0, 0, 0, 0.2)",
        };
      }

      const colorUpper = route.color.toUpperCase().trim();
      const isLightBackground = [
        "BEIGE",
        "WHITE",
        "YELLOW",
        "LIGHT BLUE",
        "LIGHT GREEN",
        "LIGHT PINK",
        "LIGHT PURPLE",
        "SILVER",
        "GOLD",
        "PINK",
        "ORANGE",
        "LAVENDER",
        "PEACH",
        "MINT",
      ].includes(colorUpper);

      if (
        colorUpper === "WHITE" ||
        bgColor.toUpperCase() === "#FFF" ||
        bgColor.toUpperCase() === "#FFFFFF"
      ) {
        return {
          background: "#f5f5f5",
          color: "#000000",
          border: "2px solid #cccccc",
        };
      }

      return {
        background: bgColor,
        color: isLightBackground ? "#000000" : "#FFFFFF",
        border: isLightBackground
          ? "1.5px solid rgba(0, 0, 0, 0.3)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      };
    },

    formatDateForPicker(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    async loadBusChangeGrades() {
      try {
        const response = await this.$http.get("api/busChange/getBusChangeGrades");
        this.busChangeGrades = Array.isArray(response) ? response : [];
      } catch (e) {
        this.busChangeGrades = [];
      }
    },

    isDayCampGrade(grade) {
      const source = ((grade && grade.source) || "").trim().toLowerCase();
      return source === "day camp" || source === "daycamp";
    },

    // Grade + Branch for School; Grade + Building for Day Camp (matches BusChanges.vue).
    getGradeSetup(grade, scope, isDayCamp = false) {
      if (!grade || !scope || !this.busChangeGrades.length) return null;
      const g = grade.trim();
      const s = scope.trim();
      return this.busChangeGrades.find((x) => {
        if (this.isDayCampGrade(x) !== isDayCamp) return false;
        if (!x.grade || x.grade.trim() !== g) return false;
        const key = isDayCamp ? x.building : x.branch;
        return key && key.trim() === s;
      });
    },

    getDeadlineGradeContext() {
      const useDayCamp = !!(this.isDayCampView && this.dayCampEnrollment);
      if (useDayCamp) {
        return {
          isDayCamp: true,
          grade:
            this.dayCampEnrollment.class ||
            this.dayCampEnrollment.grade ||
            (this.busChange && this.busChange.class) ||
            "",
          scope: this.dayCampEnrollment.building || "",
        };
      }
      return {
        isDayCamp: false,
        grade: (this.busChange && this.busChange.class) || "",
        scope: (this.busChange && this.busChange.branch) || "",
      };
    },

    async loadSelectedGradeDeadlines(grade, scope, isDayCamp = false) {
      const setup = this.getGradeSetup(grade, scope, isDayCamp);
      if (!setup || !setup.id) {
        this.selectedGradeDeadlines = [];
        return;
      }
      try {
        const response = await this.$http.get(
          "api/busChange/getBusChangeDeadlines?gradeId=" + setup.id
        );
        this.selectedGradeDeadlines = Array.isArray(response) ? response : [];
      } catch (e) {
        this.selectedGradeDeadlines = [];
      }
    },

    checkDeadlineWarning(grade, scope, startDate) {
      if (!grade || !scope) return null;
      if (!startDate || !this.selectedGradeDeadlines.length) return null;

      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(
        today.getMonth() + 1
      ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      if (startDate > todayStr) return null;

      const startDateObj = new Date(startDate + "T00:00:00");
      const dayOfWeek = startDateObj.getDay();

      const exception = this.selectedGradeDeadlines.find(
        (d) => d.exceptionDate && d.exceptionDate.startsWith(startDate)
      );
      const deadline =
        exception ||
        this.selectedGradeDeadlines.find(
          (d) => !d.exceptionDate && d.dayOfWeek === dayOfWeek
        );

      if (!deadline || !deadline.deadline) return null;

      const currentTime =
        today.getHours() * 3600 + today.getMinutes() * 60 + today.getSeconds();
      const deadlineParts = deadline.deadline.split(":");
      if (deadlineParts.length < 2) return null;

      const deadlineSeconds =
        parseInt(deadlineParts[0]) * 3600 +
        parseInt(deadlineParts[1]) * 60 +
        (deadlineParts[2] ? parseFloat(deadlineParts[2]) : 0);

      if (currentTime <= deadlineSeconds) return null;

      let hours = parseInt(deadlineParts[0]);
      const minutes = deadlineParts[1];
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      const deadlineFormatted = `${hours}:${minutes} ${ampm}`;
      return `Note: The deadline for bus changes has passed (${deadlineFormatted}).`;
    },

    notifyDeadlineWarningIfNeeded() {
      if (!this.deadlineWarningMessage) return;
      this.$notify({
        text: this.deadlineWarningMessage,
        group: "notification",
        type: "warning",
      });
    },

    formatDateTime(dateString) {
      if (!dateString) return "-";
      return moment(dateString).format("MM/DD/YYYY hh:mm A");
    },

    formatDateOnly(dateString) {
      if (!dateString) return "-";
      return moment(dateString).format("MM/DD/YYYY");
    },

    formatPhoneNumber(phoneNumber) {
      if (!phoneNumber) return "";
      // Remove all non-digit characters
      const cleaned = phoneNumber.replace(/\D/g, "");
      // Format as XXX-XXX-XXXX
      if (cleaned.length === 10) {
        return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(
          6
        )}`;
      }
      // Return original if not 10 digits
      return phoneNumber;
    },

    getBusChangeRequestIdFromBusChange(busChange) {
      if (!busChange) {
        return null;
      }

      if (busChange.busChangeRequestID) return busChange.busChangeRequestID;
      if (busChange.busChangeRequestId) return busChange.busChangeRequestId;
      if (busChange.BusChangeRequestID) return busChange.BusChangeRequestID;
      if (busChange.requestID) return busChange.requestID;
      if (busChange.requestId) return busChange.requestId;
      return null;
    },

    getCommentText(comment) {
      if (!comment) return "";
      return comment.commentText || comment.CommentText || "";
    },

    getCommentEnteredBy(comment) {
      if (!comment) return "";
      const enteredBy = comment.enteredBy || comment.EnteredBy || "";
      const familyId = this.busChange
        ? this.busChange.familyID || this.busChange.FamilyID || ""
        : "";
      const normalizeIdentity = (value) =>
        value
          ? value
              .toString()
              .trim()
              .replace(/\s+/g, "")
              .toUpperCase()
          : "";
      if (
        enteredBy &&
        familyId &&
        normalizeIdentity(enteredBy) === normalizeIdentity(familyId)
      ) {
        return "Parent";
      }
      return enteredBy;
    },

    getCommentEnteredOn(comment) {
      if (!comment) return null;
      return comment.enteredOn || comment.EnteredOn || null;
    },

    getCommentCallerID(comment) {
      if (!comment) return "";
      return comment.callerID || comment.CallerID || "";
    },

    getCommentRecordingUrl(comment) {
      if (!comment) return null;
      return normalizeRecordingUrl(
        comment.recordingURL ||
          comment.recordingUrl ||
          comment.RecordingURL ||
          null
      );
    },

    setTodayOnlyAfternoon() {
      if (this.isViewOnly) return;
      const today = moment().format("YYYY-MM-DD");
      this.formData.startDate = today;
      this.formData.endDate = today;
      this.formData.startPeriod = "PM";
      this.formData.endPeriod = "PM";
    },

    searchAccountByPhone() {
      this.openAccountSearch(this.formData.busChangePhoneNumber);
    },

    async toggleExtraInfoEdit() {
      if (!this.editingExtraInfo) {
        this.extraInfoBackup = {
          geoPhoneNumber: this.formData.geoPhoneNumber,
          note: this.formData.note,
        };
        this.editingExtraInfo = true;
        return;
      }

      if (this.busChangeId) {
        await this.saveExtraInfo();
        return;
      }

      this.editingExtraInfo = false;
      this.extraInfoBackup = null;
    },

    async saveExtraInfo() {
      const phone = this.formData.geoPhoneNumber || "";
      if (phone) {
        const digits = phone.replace(/[-\s()]/g, "");
        if (!([10, 11].includes(digits.length) && /^\d+$/.test(digits))) {
          this.$notify({
            text:
              "Geo Tracking Phone Number is invalid. Please enter a valid 10 or 11 digit phone number.",
            group: "notification",
            type: "error",
          });
          return;
        }
      }

      this.savingExtraInfo = true;
      try {
        const response = await this.$http.post(
          "api/BusChange/UpdateExtraInfo",
          {
            id: this.busChangeId,
            geoPhoneNumber: this.formData.geoPhoneNumber || "",
            note: this.formData.note || "",
          }
        );

        if (response && response.geoSyncError) {
          this.$notify({
            text:
              "Saved, but GeoTrack sync failed: " + response.geoSyncError,
            group: "notification",
            type: "warning",
          });
        } else {
          this.$notify({
            text: "Geo phone and note saved",
            group: "notification",
            type: "success",
          });
        }

        this.editingExtraInfo = false;
        this.extraInfoBackup = null;
      } catch (e) {
        this.$notify({
          text: "Failed to save geo phone / note",
          group: "notification",
          type: "error",
        });
      } finally {
        this.savingExtraInfo = false;
      }
    },

    cancelExtraInfoEdit() {
      if (this.extraInfoBackup) {
        this.formData.geoPhoneNumber = this.extraInfoBackup.geoPhoneNumber;
        this.formData.note = this.extraInfoBackup.note;
      }
      this.editingExtraInfo = false;
      this.extraInfoBackup = null;
    },

    async toggleEndInfoEdit() {
      if (!this.editingEndInfo) {
        this.endEditReturnRoute = this.endedReturnRoute || null;
        this.endEditReturnStopNumber = this.endedReturnStopNumber || null;
        this.endEditReturnRouteStops = [];
        this.editingEndInfo = true;
        if (this.endEditReturnRoute) {
          await this.loadEndReturnStops(
            this.endEditReturnRoute.id,
            this.endEditReturnStopNumber
          );
        }
        return;
      }

      await this.saveEndInfo();
    },

    cancelEndInfoEdit() {
      this.editingEndInfo = false;
      this.endEditReturnRoute = null;
      this.endEditReturnStopNumber = null;
      this.endEditReturnRouteStops = [];
    },

    async onEndEditReturnRouteChange(route) {
      this.endEditReturnStopNumber = null;
      this.endEditReturnRouteStops = [];
      if (!route || !route.id) return;
      await this.loadEndReturnStops(route.id);
    },

    async loadEndReturnStops(routeId, preferredStopNumber = null) {
      if (!routeId) return;
      this.loadingEndReturnStops = true;
      try {
        const stops = await this.$http.get(
          `api/buschange/GetRouteStops?routeId=${routeId}`
        );
        this.endEditReturnRouteStops = Array.isArray(stops) ? stops : [];
        if (
          preferredStopNumber != null &&
          this.endEditReturnRouteStops.some(
            (s) => s.stopNumber === preferredStopNumber
          )
        ) {
          this.endEditReturnStopNumber = preferredStopNumber;
        }
      } catch (e) {
        this.endEditReturnRouteStops = [];
      } finally {
        this.loadingEndReturnStops = false;
      }
    },

    async saveEndInfo() {
      if (!this.endEditReturnRoute || !this.endEditReturnStopNumber) {
        this.$notify({
          text: "Return route and stop are required",
          group: "notification",
          type: "error",
        });
        return;
      }

      this.savingEndInfo = true;
      try {
        const response = await this.$http.post(
          "api/buschange/UpdateEndBusChangeInfo",
          {
            id: this.busChangeId,
            returnRouteID: this.endEditReturnRoute.id,
            returnStopNumber: String(this.endEditReturnStopNumber),
          }
        );

        if (response && response.error) {
          this.$notify({
            text: response.error,
            group: "notification",
            type: "error",
          });
          return;
        }

        if (this.busChange) {
          this.busChange.returnRouteID = this.endEditReturnRoute.id;
          this.busChange.returnStopNumber = String(
            this.endEditReturnStopNumber
          );
        }

        this.$notify({
          text: "Return bus/stop updated",
          group: "notification",
          type: "success",
        });

        this.editingEndInfo = false;
        this.endEditReturnRoute = null;
        this.endEditReturnStopNumber = null;
        this.endEditReturnRouteStops = [];
      } catch (e) {
        const msg =
          (e && e.body && (e.body.message || e.body.title || e.body)) ||
          (e && e.message) ||
          "Failed to update return bus/stop";
        this.$notify({
          text: typeof msg === "string" ? msg : "Failed to update return bus/stop",
          group: "notification",
          type: "error",
        });
      } finally {
        this.savingEndInfo = false;
      }
    },

    getPeriodOrder(period) {
      if (period === "PM") return 2;
      return 1;
    },

    formatPeriodLabel(period) {
      if (!period) return "-";
      const normalized = period
        .toString()
        .trim()
        .toUpperCase();
      if (normalized === "AM") return "Morning Pickup";
      if (normalized === "PM") return "Afternoon Dropoff";
      return period;
    },

    isPendingStatus(status) {
      if (!status) return false;
      return status.toString().trim().toUpperCase() === "PENDING";
    },

    hasPendingLinkedRequest() {
      if (!this.linkedRequest) return false;
      const requestId =
        this.linkedRequest.id ||
        this.linkedRequest.busChangeRequestID ||
        this.linkedRequest.busChangeRequestId ||
        null;
      return (
        requestId &&
        this.isPendingStatus(this.linkedRequest.status || this.linkedRequest.Status)
      );
    },

    getPendingRevertedToDate() {
      if (!this.linkedRequest) return null;
      return (
        this.linkedRequest.statusRevertedToPendingOn ||
        this.linkedRequest.StatusRevertedToPendingOn ||
        null
      );
    },

    isCommentAwaitingResponse(comment, index, comments) {
      if (!this.hasPendingLinkedRequest()) return false;
      const revertedToPendingOn = this.getPendingRevertedToDate();

      if (revertedToPendingOn) {
        const enteredOn = this.getCommentEnteredOn(comment);
        if (!enteredOn) return false;
        return moment(enteredOn).isSameOrAfter(moment(revertedToPendingOn));
      }

      return comments && index === comments.length - 1;
    },

    getIncompleteRouteMessage() {
      const hasPickup =
        this.selectedPickupRoute && this.selectedPickupRoute.id;
      const hasDropoff =
        this.selectedDropoffRoute && this.selectedDropoffRoute.id;

      if (hasPickup && !hasDropoff) {
        return "You have only selected the Pickup route.";
      }
      if (!hasPickup && hasDropoff) {
        return "You have only selected the Dropoff route.";
      }
      return "You have not selected any route.";
    },

    isEndPeriodBeforeStartPeriod(startPeriod, endPeriod) {
      return this.getPeriodOrder(endPeriod) < this.getPeriodOrder(startPeriod);
    },

    async loadBusChangeComments(busChangeRequestId) {
      this.busChangeComments = [];

      if (!busChangeRequestId) {
        return;
      }

      this.loadingBusChangeComments = true;
      try {
        const response = await this.$http.get(
          `api/BusChange/GetBusChangeComments?busChangeRequestId=${busChangeRequestId}`
        );
        this.busChangeComments = Array.isArray(response) ? response : [];
      } catch (e) {
        this.busChangeComments = [];
      } finally {
        this.loadingBusChangeComments = false;
      }
    },

    async loadLinkedRequestForBusChange(busChangeID) {
      this.linkedRequest = null;
      if (!busChangeID) return;
      try {
        const request = await this.$http.get(
          `api/BusChangeRequests/GetLinkedRequest?busChangeID=${busChangeID}`
        );
        this.linkedRequest = request || null;
      } catch (e) {
        this.linkedRequest = null;
      }
    },

    getTeacherDisplay() {
      if (!this.busChange) return "-";
      const teachers = [];
      if (this.busChange.teacherH) teachers.push(this.busChange.teacherH);
      if (this.busChange.teacherH2) teachers.push(this.busChange.teacherH2);
      return teachers.join(" / ") || "-";
    },

    getDcTeacher(student) {
      if (!student) return "";
      const teachers = [];
      if (student.teacherH) teachers.push(student.teacherH);
      if (student.teacherH2) teachers.push(student.teacherH2);
      return teachers.join(" / ");
    },

    async loadDayCampInfoForView() {
      this.dayCampEnrollment = null;
      // Only relevant when this bus change overlaps the day camp season.
      if (!this.isDayCampView || !this.busChange) return;

      const accountID = (
        this.busChange.familyID ||
        this.busChange.FamilyID ||
        ""
      )
        .toString()
        .trim();
      const studentID = (this.busChange.studentID || "").toString().trim();
      if (!accountID || !studentID) return;

      try {
        const students = await this.$http.get(
          `api/daycamp/getEnrolledStudentsByAccountID?accountID=${accountID}`
        );
        if (!students || !students.length) return;

        const match = students.find(
          (s) =>
            s.studentID &&
            s.studentID.toString().trim().toUpperCase() ===
              studentID.toUpperCase()
        );
        this.dayCampEnrollment = match || null;
      } catch (e) {
        this.dayCampEnrollment = null;
      }
    },

    getEnrollmentSummary(student) {
      if (!student) return "";
      const type = (student.regType || "").trim();
      if (!type) return "";
      const weekCount = (student.weeks || []).length;
      if (type.toUpperCase() === "CUSTOM") {
        return `Custom (${weekCount} week${weekCount === 1 ? "" : "s"})`;
      }
      return type;
    },

    getEnrollmentDisplay(student) {
      if (!student) return "";
      const type = (student.regType || "").trim();
      if (!type) return "";
      const detail = this.getEnrollmentWeeksLabel(student);
      if (type.toUpperCase() === "CUSTOM") {
        const weekCount = (student.weeks || []).length;
        const label = `Custom (${weekCount} week${
          weekCount === 1 ? "" : "s"
        })`;
        return detail
          ? `Enrolled for ${label}: ${detail}`
          : `Enrolled for ${label}`;
      }
      return detail
        ? `Enrolled for ${type} (${detail})`
        : `Enrolled for ${type}`;
    },

    getEnrollmentWeeksLabel(student) {
      if (!student) return "";
      const weeks = student.weeks || [];
      const type = (student.regType || "").trim().toUpperCase();

      // For non-custom enrollments (FULL / HALF / etc.) show the overall
      // enrolled range instead of listing every week.
      if (type !== "CUSTOM") {
        const start =
          student.enrollmentStartDate ||
          (weeks.length ? weeks[0].startDate : null);
        const end =
          student.enrollmentEndDate ||
          (weeks.length ? weeks[weeks.length - 1].endDate : null);
        if (start && end) {
          return `${moment(start).format("MMM D")}–${moment(end).format(
            "MMM D"
          )}`;
        }
        return "";
      }

      if (!weeks.length) {
        if (student.enrollmentStartDate && student.enrollmentEndDate) {
          return `${moment(student.enrollmentStartDate).format(
            "MMM D"
          )}–${moment(student.enrollmentEndDate).format("MMM D")}`;
        }
        return "";
      }
      return weeks
        .map((w) => {
          const label = w.parshe ? w.parshe : `Week ${w.weekNumber}`;
          const dates =
            w.startDate && w.endDate
              ? ` (${moment(w.startDate).format("MMM D")}–${moment(
                  w.endDate
                ).format("MMM D")})`
              : "";
          return label + dates;
        })
        .join(", ");
    },

    getEnrollmentDateWarning() {
      if (!this.dayCampEnrollment) return null;

      const weeks = this.dayCampEnrollment.weeks || [];
      if (!weeks.length) return null;

      const start = this.formData.startDate;
      const end = this.formData.endDate;
      if (!start || !end) return null;

      const reqStart = moment(start, "YYYY-MM-DD").startOf("day");
      const reqEnd = moment(end, "YYYY-MM-DD").startOf("day");
      if (!reqStart.isValid() || !reqEnd.isValid()) return null;
      if (reqEnd.isBefore(reqStart)) return null;

      const ranges = weeks
        .filter((w) => w.startDate && w.endDate)
        .map((w) => ({
          s: moment(w.startDate).startOf("day"),
          e: moment(w.endDate).startOf("day"),
        }))
        .filter((r) => r.s.isValid() && r.e.isValid());
      if (!ranges.length) return null;

      // Every day in the requested range must fall within an enrolled week.
      // A small tolerance bridges weekend gaps between adjacent enrolled weeks
      // while still flagging real multi-week gaps (e.g. custom weeks 1-3, 7-9).
      const covers = (day) =>
        ranges.some(
          (r) =>
            day.isSameOrAfter(r.s) &&
            day.isSameOrBefore(r.e.clone().add(2, "days"))
        );

      const cursor = reqStart.clone();
      let hasUncovered = false;
      while (cursor.isSameOrBefore(reqEnd)) {
        if (!covers(cursor)) {
          hasUncovered = true;
          break;
        }
        cursor.add(1, "day");
      }
      if (!hasUncovered) return null;

      return (
        "This student is only enrolled in day camp for: " +
        this.getEnrollmentWeeksLabel(this.dayCampEnrollment) +
        ". The selected date range includes dates outside their enrollment."
      );
    },

    confirmSaveWithEnrollmentWarning() {
      this.enrollmentWarningDialog = false;
      this.skipEnrollmentWarning = true;
      this.save();
    },

    cancelEnrollmentWarning() {
      this.enrollmentWarningDialog = false;
      this.skipEnrollmentWarning = false;
    },

    async onPickupRouteChange(pickupRoute) {
      this.selectedPickupStopNumber = null;
      this.pickupRouteStops = [];

      if (!pickupRoute) {
        return;
      }

      await this.loadRouteStops("pickup", pickupRoute.id);

      // Helper to check if two comma-separated strings have any overlap
      const hasOverlap = (str1, str2) => {
        if (!str1 || !str2) return false;
        const arr1 = str1.split(",").map((s) => s.trim());
        const arr2 = str2.split(",").map((s) => s.trim());
        return arr1.some((item) => arr2.includes(item));
      };

      const pickupBusNumber = this.getRouteBusNumber(pickupRoute);

      // Try to find exact match first (highest priority)
      const exactMatch = this.filteredDropoffRoutes.find((dropoff) => {
        return (
          this.getRouteBusNumber(dropoff) === pickupBusNumber &&
          dropoff.driver === pickupRoute.driver &&
          dropoff.buildings === pickupRoute.buildings &&
          dropoff.grades === pickupRoute.grades &&
          dropoff.driver_Bus_ID === pickupRoute.driver_Bus_ID
        );
      });

      if (exactMatch) {
        this.selectedDropoffRoute = exactMatch;
        await this.onDropoffRouteChange(exactMatch);
        return;
      }

      // Try match with overlapping buildings and grades
      const overlapMatch = this.filteredDropoffRoutes.find((dropoff) => {
        return (
          this.getRouteBusNumber(dropoff) === pickupBusNumber &&
          dropoff.driver === pickupRoute.driver &&
          hasOverlap(dropoff.buildings, pickupRoute.buildings) &&
          hasOverlap(dropoff.grades, pickupRoute.grades) &&
          dropoff.driver_Bus_ID === pickupRoute.driver_Bus_ID
        );
      });

      if (overlapMatch) {
        this.selectedDropoffRoute = overlapMatch;
        await this.onDropoffRouteChange(overlapMatch);
        return;
      }

      // Try looser match - just bus number, driver, and overlapping grades
      const looseMatch = this.filteredDropoffRoutes.find((dropoff) => {
        return (
          this.getRouteBusNumber(dropoff) === pickupBusNumber &&
          dropoff.driver === pickupRoute.driver &&
          hasOverlap(dropoff.grades, pickupRoute.grades)
        );
      });

      if (looseMatch) {
        this.selectedDropoffRoute = looseMatch;
        await this.onDropoffRouteChange(looseMatch);
      }
    },

    async onDropoffRouteChange(dropoffRoute) {
      this.selectedDropoffStopNumber = null;
      this.dropoffRouteStops = [];

      if (!dropoffRoute) {
        return;
      }

      await this.loadRouteStops("dropoff", dropoffRoute.id);
      this.copyPickupStopToDropoffIfSameRoute(this.selectedPickupStopNumber);
    },

    onPickupStopNumberChange(stopNumber) {
      this.copyPickupStopToDropoffIfSameRoute(stopNumber);
    },

    isSameBusRoute(pickupRoute, dropoffRoute) {
      if (!pickupRoute || !dropoffRoute) return false;
      const pickupBus = this.getRouteBusNumber(pickupRoute);
      const dropoffBus = this.getRouteBusNumber(dropoffRoute);
      return !!pickupBus && pickupBus === dropoffBus;
    },

    copyPickupStopToDropoffIfSameRoute(stopNumber) {
      if (stopNumber == null || stopNumber === "") return;
      if (
        !this.isSameBusRoute(
          this.selectedPickupRoute,
          this.selectedDropoffRoute
        )
      ) {
        return;
      }
      if (
        !this.dropoffRouteStops ||
        !this.dropoffRouteStops.some((s) => s.stopNumber === stopNumber)
      ) {
        return;
      }
      this.selectedDropoffStopNumber = stopNumber;
    },

    async loadRouteStops(direction, routeId, preferredStopNumber = null) {
      if (!routeId) {
        return;
      }

      const loadingKey =
        direction === "pickup" ? "loadingPickupStops" : "loadingDropoffStops";
      const itemsKey =
        direction === "pickup" ? "pickupRouteStops" : "dropoffRouteStops";
      const selectedKey =
        direction === "pickup"
          ? "selectedPickupStopNumber"
          : "selectedDropoffStopNumber";

      this[loadingKey] = true;
      try {
        const stops = await this.$http.get(
          `api/buschange/GetRouteStops?routeId=${routeId}`
        );
        this[itemsKey] = Array.isArray(stops) ? stops : [];
        if (
          preferredStopNumber != null &&
          this[itemsKey].some((s) => s.stopNumber === preferredStopNumber)
        ) {
          this[selectedKey] = preferredStopNumber;
        }
      } catch (e) {
        this[itemsKey] = [];
      } finally {
        this[loadingKey] = false;
      }
    },

    getMissingGeoStopMessage() {
      const missing = [];
      if (this.selectedPickupRoute && !this.selectedPickupStopNumber) {
        missing.push("pickup");
      }
      if (this.selectedDropoffRoute && !this.selectedDropoffStopNumber) {
        missing.push("dropoff");
      }
      if (!missing.length) return "";
      if (missing.length === 2) {
        return "Pickup and dropoff routes are assigned, but no Geo stop was selected for either one.";
      }
      return `A ${missing[0]} route is assigned, but no Geo stop was selected.`;
    },

    validateForm() {
      // Check required fields
      if (!this.formData.startDate) {
        this.$notify({
          text: "Start Date is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (!this.formData.busChangeAddress) {
        this.$notify({
          text: "Bus Change Address is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (!this.formData.startPeriod) {
        this.$notify({
          text: "Starting Run is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (!this.formData.endPeriod) {
        this.$notify({
          text: "Ending After is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      // Validate phone numbers if they have values
      const validatePhone = (phoneNumber) => {
        if (!phoneNumber) return true; // Empty is ok (not required)
        const digits = phoneNumber.replace(/[-\s()]/g, "");
        return [10, 11].includes(digits.length) && /^\d+$/.test(digits);
      };

      if (
        this.formData.busChangePhoneNumber &&
        !validatePhone(this.formData.busChangePhoneNumber)
      ) {
        this.$notify({
          text:
            "Bus Change Phone Number is invalid. Please enter a valid 10 or 11 digit phone number.",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (
        this.formData.geoPhoneNumber &&
        !validatePhone(this.formData.geoPhoneNumber)
      ) {
        this.$notify({
          text:
            "Geo Phone Number is invalid. Please enter a valid 10 or 11 digit phone number.",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (
        this.formData.notificationPhoneNumber &&
        !validatePhone(this.formData.notificationPhoneNumber)
      ) {
        this.$notify({
          text:
            "Notification Phone Number is invalid. Please enter a valid 10 or 11 digit phone number.",
          group: "notification",
          type: "error",
        });
        return false;
      }

      // Validate endDate is not before startDate
      if (this.formData.endDate && this.formData.startDate) {
        if (this.formData.endDate < this.formData.startDate) {
          this.$notify({
            text: "End Date cannot be before Start Date",
            group: "notification",
            type: "error",
          });
          return false;
        }

        if (
          this.formData.endDate === this.formData.startDate &&
          this.isEndPeriodBeforeStartPeriod(
            this.formData.startPeriod,
            this.formData.endPeriod
          )
        ) {
          this.$notify({
            text: "Ending After cannot be before Starting Run on the same day.",
            group: "notification",
            type: "error",
          });
          return false;
        }
      }

      return true;
    },

    openAccountSearch(prefillPhone) {
      if (this.isViewOnly) return;
      const phone =
        typeof prefillPhone === "string" ? prefillPhone : "";
      // Reset dialog search state
      this.dialogPhoneSearch = phone || "";
      this.dialogFirstNameSearch = "";
      this.dialogLastNameSearch = "";
      this.dialogHouseNumberSearch = "";
      this.dialogStreetSearch = "";
      this.dialogAccounts = [];
      this.dialogShowNoAccountsMessage = false;
      this.accountSearchDialog = true;
      if (this.dialogPhoneSearch) {
        this.searchDialogByPhone(this.dialogPhoneSearch);
      }
    },

    closeAccountSearchDialog() {
      this.accountSearchDialog = false;
      this.dialogPhoneSearch = "";
      this.dialogFirstNameSearch = "";
      this.dialogLastNameSearch = "";
      this.dialogHouseNumberSearch = "";
      this.dialogStreetSearch = "";
      this.dialogAccounts = [];
      this.dialogShowNoAccountsMessage = false;
    },

    async searchDialogByPhone(value) {
      // Clear previous timeout
      if (this.dialogSearchDebounceTimer) {
        clearTimeout(this.dialogSearchDebounceTimer);
      }

      const phoneNumber = value ? value.trim() : "";

      if (!phoneNumber || phoneNumber.length < 10) {
        this.dialogAccounts = [];
        this.dialogShowNoAccountsMessage = false;
        return;
      }

      this.dialogShowNoAccountsMessage = false;

      // Debounce the search
      this.dialogSearchDebounceTimer = setTimeout(async () => {
        try {
          const accounts = await this.$http.get(
            `api/students/getAccountByPhoneNumber?phoneNumber=${phoneNumber}`
          );

          this.dialogAccounts = accounts || [];

          if (this.dialogAccounts.length === 0) {
            this.dialogShowNoAccountsMessage = true;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
          this.dialogAccounts = [];
        }
      }, 400);
    },

    async searchDialogByName() {
      // Clear previous timeout
      if (this.dialogSearchDebounceTimer) {
        clearTimeout(this.dialogSearchDebounceTimer);
      }

      const lastName = this.dialogLastNameSearch
        ? this.dialogLastNameSearch.trim()
        : "";
      const firstName = this.dialogFirstNameSearch
        ? this.dialogFirstNameSearch.trim()
        : "";

      if (!lastName && !firstName) {
        this.dialogAccounts = [];
        this.dialogShowNoAccountsMessage = false;
        return;
      }

      this.dialogShowNoAccountsMessage = false;

      // Debounce the search
      this.dialogSearchDebounceTimer = setTimeout(async () => {
        try {
          const url = `api/students/getAccountsByName?last=${lastName}&first=${firstName}&isYiddish=${this
            .dialogSearchNameMode === "yiddish"}`;

          const accounts = await this.$http.get(url);

          this.dialogAccounts = accounts || [];

          if (this.dialogAccounts.length === 0) {
            this.dialogShowNoAccountsMessage = true;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
          this.dialogAccounts = [];
        }
      }, 400);
    },

    async searchDialogByAddress() {
      // Clear previous timeout
      if (this.dialogSearchDebounceTimer) {
        clearTimeout(this.dialogSearchDebounceTimer);
      }

      const houseNumber = this.dialogHouseNumberSearch
        ? this.dialogHouseNumberSearch.trim()
        : "";
      const street = this.dialogStreetSearch
        ? this.dialogStreetSearch.trim()
        : "";

      if (!houseNumber && !street) {
        this.dialogAccounts = [];
        this.dialogShowNoAccountsMessage = false;
        return;
      }

      this.dialogShowNoAccountsMessage = false;

      // Debounce the search
      this.dialogSearchDebounceTimer = setTimeout(async () => {
        try {
          const params = new URLSearchParams();
          if (houseNumber) params.append("houseNumber", houseNumber);
          if (street) params.append("street", street);
          if (this.userBranch) params.append("branch", this.userBranch);

          const url = `api/students/getAccountsByAddress?${params.toString()}`;
          const accounts = await this.$http.get(url);

          this.dialogAccounts = accounts || [];

          if (this.dialogAccounts.length === 0) {
            this.dialogShowNoAccountsMessage = true;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
          this.dialogAccounts = [];
        }
      }, 400);
    },

    onAccountSelected(account) {
      this.accountSearchDialog = false;
      this.formData.busChangeAccountID = account.accountID;
      // User explicitly picked this account — replace whatever was entered
      this.formData.busChangeName = (account.lastName || "").trim();
      if (account.address1) {
        this.formData.busChangeAddress = account.address1;
      }
      if (!this.formData.busChangePhoneNumber) {
        const accountPhone = account.homePhone || account.cellPhone || "";
        this.formData.busChangePhoneNumber = accountPhone
          ? this.formatPhoneNumber(accountPhone) || accountPhone
          : "";
      }

      // Clear dialog search state
      this.dialogPhoneSearch = "";
      this.dialogFirstNameSearch = "";
      this.dialogLastNameSearch = "";
      this.dialogHouseNumberSearch = "";
      this.dialogStreetSearch = "";
      this.dialogAccounts = [];
      this.dialogShowNoAccountsMessage = false;
    },

    // Notification dialog handlers
    proceedWithNotification() {
      this.notificationConfirmDialog = false;
      this.skipNotification = true;
      this.savedBusChangeId = true; // Flag to send notification after save
      this.save();
    },

    cancelSave() {
      this.notificationConfirmDialog = false;
      this.missingNotificationFieldsDialog = false;
      this.clearedNotificationWarningDialog = false;
      this.skipRouteWarning = false;
      this.skipEnrollmentWarning = false;
      this.enrollmentWarningDialog = false;
      this.skipNotification = false;
      this.savedBusChangeId = null;
    },

    saveWithoutNotification() {
      this.missingNotificationFieldsDialog = false;
      this.skipNotification = true;
      this.savedBusChangeId = null; // Don't send notification
      this.save();
    },

    closeMissingFieldsDialog() {
      this.missingNotificationFieldsDialog = false;
      this.skipNotification = false;
      this.savedBusChangeId = null;
      // User can now fill in the fields and try saving again
    },

    cancelClearedNotificationWarning() {
      this.clearedNotificationWarningDialog = false;
      this.skipNotification = false;
      // User stays in edit mode to add notification fields back
    },

    confirmSaveWithoutNotification() {
      this.clearedNotificationWarningDialog = false;
      this.skipNotification = true;
      this.savedBusChangeId = null; // Don't send notification
      this.save();
    },

    confirmSaveWithIncompleteRoute() {
      this.incompleteRouteDialog = false;
      this.skipRouteWarning = true;
      this.save();
    },

    confirmSaveWithoutGeoStop() {
      this.missingGeoStopDialog = false;
      this.skipGeoStopWarning = true;
      this.save();
    },

    async sendBusChangeNotification(busChangeId) {
      this.sendingNotification = true;
      try {
        const apiUrl = `https://broadcast-3t6ck5zrrq-ue.a.run.app/busChangeNotification/sendBusChangeNotification?busChangeId=${busChangeId}`;
        const response = await this.$http.post(apiUrl, {});
        const notices = getBusChangeNotificationNotices(response);
        notices.forEach((notice) => {
          this.$notify({
            text: notice.text,
            group: "notification",
            type: notice.type,
            duration: 8000,
          });
        });
      } catch (error) {
        this.$notify({
          text:
            "Failed to send bus change notification. The bus change was saved but notification failed.",
          group: "notification",
          type: "warning",
        });
      } finally {
        this.sendingNotification = false;
      }
    },

    async save() {
      if (this.isViewOnly) return;

      if (!this.validateForm()) {
        return;
      }
      this.notifyDeadlineWarningIfNeeded();

      // Day camp: warn (soft) if the date range falls outside enrolled weeks.
      if (!this.skipEnrollmentWarning && this.getEnrollmentDateWarning()) {
        this.enrollmentWarningDialog = true;
        return;
      }

      // Check if notification fields were cleared for IVR sources
      if (!this.skipNotification) {
        const hadNotificationInfo =
          this.originalNotificationPhone || this.originalNotificationType;
        const currentHasNotificationInfo =
          this.formData.notificationPhoneNumber &&
          this.formData.notificationType;

        if (hadNotificationInfo && !currentHasNotificationInfo) {
          this.clearedNotificationWarningDialog = true;
          return;
        }
      }

      // Check for incomplete route selection warning rules
      if (!this.skipRouteWarning) {
        const hasPickup =
          this.selectedPickupRoute && this.selectedPickupRoute.id;
        const hasDropoff =
          this.selectedDropoffRoute && this.selectedDropoffRoute.id;
        const originalHasPickup = !!this.originalPickupRouteID;
        const originalHasDropoff = !!this.originalDropoffRouteID;
        const originallyNoRoutes = !originalHasPickup && !originalHasDropoff;
        const hasOnlyOneRoute =
          (hasPickup && !hasDropoff) || (!hasPickup && hasDropoff);

        if ((!hasPickup && !hasDropoff) || (hasOnlyOneRoute && originallyNoRoutes)) {
          this.incompleteRouteDialog = true;
          return;
        }
      }

      // Warn when a route is assigned without a Geo stop (GeoTrack will not sync)
      if (!this.skipGeoStopWarning) {
        const missingGeoStopMsg = this.getMissingGeoStopMessage();
        if (missingGeoStopMsg) {
          this.missingGeoStopMessage = missingGeoStopMsg;
          this.missingGeoStopDialog = true;
          return;
        }
      }

      // Check if notification should be sent
      if (!this.skipNotification) {
        const currentPickupID = this.selectedPickupRoute
          ? this.selectedPickupRoute.id
          : null;
        const currentDropoffID = this.selectedDropoffRoute
          ? this.selectedDropoffRoute.id
          : null;

        // Check if routes were changed or assigned for the first time
        const routesChanged =
          currentPickupID !== this.originalPickupRouteID ||
          currentDropoffID !== this.originalDropoffRouteID;

        // Allow notification when at least one route is assigned
        const atLeastOneRouteSelected = currentPickupID || currentDropoffID;
        const shouldSendNotification =
          this.sendNotificationOnSave ||
          routesChanged ||
          this.hasPendingLinkedRequest();

        if (shouldSendNotification && atLeastOneRouteSelected) {
          // Check if notification fields are filled
          const hasNotificationPhone = this.formData.notificationPhoneNumber;
          const hasNotificationType = this.formData.notificationType;

          if (!hasNotificationPhone || !hasNotificationType) {
            // Show missing fields dialog
            this.missingNotificationFieldsDialog = true;
            return;
          } else {
            // Show notification confirmation dialog
            this.notificationConfirmDialog = true;
            return;
          }
        }
      }

      this.saving = true;
      try {
        const data = {
          studentID: this.busChange.studentID,
          startDate: this.formData.startDate
            ? moment(this.formData.startDate).format("MM/DD/YYYY")
            : null,
          endDate: this.formData.endDate
            ? moment(this.formData.endDate).format("MM/DD/YYYY")
            : null,
          startPeriod: this.formData.startPeriod,
          endPeriod: this.formData.endPeriod,
          homeAddress: this.busChange.homeAddress || "",
          busChangeAddress: this.formData.busChangeAddress,
          busChangePhoneNumber: this.formData.busChangePhoneNumber,
          geoPhoneNumber: this.formData.geoPhoneNumber,
          busChangeName: this.formData.busChangeName,
          busChangeAccountID: this.formData.busChangeAccountID,
          notificationPhoneNumber: this.formData.notificationPhoneNumber,
          notificationType: this.formData.notificationType,
          pickupRouteID: this.selectedPickupRoute
            ? this.selectedPickupRoute.id
            : null,
          dropoffRouteID: this.selectedDropoffRoute
            ? this.selectedDropoffRoute.id
            : null,
          pickupStopNumber: this.selectedPickupStopNumber,
          dropoffStopNumber: this.selectedDropoffStopNumber,
          note: this.formData.note,
          building: this.busChange.building,
          branch: this.busChange.branch,
          class: this.busChange.class,
          section: this.busChange.section,
          term: this.busChange.term,
          enteredBy: this.$store.state.authentication.user,
          source: this.busChange.source || "ADMIN PORTAL",
        };

        // Include ID when editing
        if (this.busChangeId) {
          data.id = this.busChangeId;
        }

        const response = await this.$http.post("api/buschange/save", data);

        if (response.error) {
          this.$notify({
            group: "notification",
            type: "error",
            text: response.error,
          });
          return;
        }

        // Get the bus change ID from response
        const busChangeId = response.id || this.busChangeId;

        this.$notify({
          text: "Bus change updated successfully",
          group: "notification",
          type: "success",
        });

        if (response.geoSyncError) {
          this.$notify({
            group: "notification",
            type: "error",
            text: response.geoSyncError,
          });
        }

        // Send notification if needed
        if (this.savedBusChangeId && busChangeId) {
          await this.sendBusChangeNotification(busChangeId);
        }

        this.$emit("saved");
        this.close();
      } catch (e) {
        this.$notify({
          text: "Failed to update bus change",
          group: "notification",
          type: "error",
        });
      } finally {
        this.saving = false;
        this.skipRouteWarning = false;
        this.skipGeoStopWarning = false;
        this.skipEnrollmentWarning = false;
        this.skipNotification = false;
        this.savedBusChangeId = null;
      }
    },

    openDeleteDialog() {
      if (!this.busChangeId) return;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      if (!this.busChangeId) return;

      this.deleting = true;
      try {
        await this.$http.post("api/busChange/delete", [this.busChangeId]);

        this.$notify({
          text: "Bus change deleted successfully",
          group: "notification",
          type: "success",
        });

        this.deleteDialog = false;
        this.$emit("saved");
        this.close();
      } catch (e) {
        this.$notify({
          text: "Failed to delete bus change",
          group: "notification",
          type: "error",
        });
      } finally {
        this.deleting = false;
      }
    },

    close() {
      this.internalDialog = false;
      this.deleteDialog = false;
      this.incompleteRouteDialog = false;
      this.missingGeoStopDialog = false;
      this.busChange = null;
      this.linkedRequest = null;
      this.editingExtraInfo = false;
      this.savingExtraInfo = false;
      this.extraInfoBackup = null;
      this.busChangeComments = [];
      this.loadingBusChangeComments = false;
      this.editingEndInfo = false;
      this.savingEndInfo = false;
      this.endEditReturnRoute = null;
      this.endEditReturnStopNumber = null;
      this.endEditReturnRouteStops = [];
      this.loadingEndReturnStops = false;
      this.formData = {
        startDate: "",
        endDate: "",
        startPeriod: "",
        endPeriod: "",
        busChangeAddress: "",
        busChangePhoneNumber: "",
        geoPhoneNumber: "",
        busChangeName: "",
        busChangeAccountID: "",
        notificationPhoneNumber: "",
        notificationType: "",
        note: "",
      };
      this.selectedPickupRoute = null;
      this.selectedDropoffRoute = null;
      this.selectedPickupStopNumber = null;
      this.selectedDropoffStopNumber = null;
      this.pickupRouteStops = [];
      this.dropoffRouteStops = [];
      this.loadingPickupStops = false;
      this.loadingDropoffStops = false;
      this.selectedGradeDeadlines = [];
      this.busChangeGrades = [];
      this.originalPickupRouteID = null;
      this.originalDropoffRouteID = null;
      this.originalNotificationPhone = null;
      this.originalNotificationType = null;
      this.skipRouteWarning = false;
      this.skipGeoStopWarning = false;
      this.missingGeoStopMessage = "";
      this.skipEnrollmentWarning = false;
      this.enrollmentWarningDialog = false;
      this.dayCampEnrollment = null;
      this.skipNotification = false;
      this.savedBusChangeId = null;
    },
  },
};
</script>

<style scoped>
.bus-change-dialog .dialog-header {
  background: linear-gradient(135deg, rgb(92, 184, 96) 0%, #388e3c 100%);
  color: white;
  padding: 0px 16px;
}

.bus-change-dialog .dialog-title {
  font-size: 20px;
  font-weight: 400;
}

.section-header {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: rgb(92, 184, 96);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e8f5e9;
}

.dialog-body {
  background: #f2f4f2;
}

.form-section-card {
  background: #ffffff;
  border: 1px solid #e2e8e2 !important;
  border-radius: 8px !important;
}

.geo-stop-picker {
  background: #f7faf7;
  border: 1px dashed #c5d6c5;
  border-radius: 8px;
  padding: 8px 8px 2px;
}

.geo-stop-selection {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.geo-stop-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: #2e7d32;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.geo-stop-address {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.geo-stop-item-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.selected-student-card {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border: 2px solid rgb(92, 184, 96) !important;
  border-radius: 12px;
  padding: 8px;
}

.ended-bus-change-card {
  background: linear-gradient(135deg, #455a64 0%, #37474f 100%);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.22);
}

.ended-bus-change-header {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  color: #ffffff;
}

.ended-bus-change-title-block {
  min-width: 0;
}

.ended-bus-change-title {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.ended-bus-change-date {
  font-weight: 500;
  opacity: 0.85;
}

.ended-bus-change-btn {
  border-color: rgba(255, 255, 255, 0.55) !important;
}

.ended-bus-change-body {
  background: #ffffff;
  padding: 10px 14px 12px;
}

.ended-bus-change-rows {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ended-bus-change-row {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #37474f;
}

.ended-bus-change-row .v-icon {
  color: #607d8b;
}

.ended-bus-change-missing {
  color: #9e9e9e;
  font-style: italic;
}

.ended-bus-change-edit {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.record-info-with-audio {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.record-info-compact {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 11px;
  color: #757575;
  padding: 4px 8px;
  background: #fafafa;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  flex: 1;
  min-width: 300px;
}

.record-info-compact .info-item {
  display: inline-flex;
  align-items: center;
}

.record-info-compact .info-divider {
  margin: 0 8px;
  color: #bdbdbd;
}

.recording-inline {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 8px 12px;
  background: linear-gradient(135deg, #fff5f5 0%, #fff 100%);
  border-radius: 8px;
  border: 1px solid #ffcccc;
  box-shadow: 0 1px 3px rgba(255, 107, 107, 0.1);
  min-width: 280px;
}

.recording-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.recording-icon {
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

.recording-label {
  font-size: 11px;
  font-weight: 600;
  color: #d63031;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.recording-player {
  width: 100%;
  max-width: 280px;
  height: 32px;
  border-radius: 4px;
}

.comments-card {
  background: #ffffff;
  border: 1px solid #e2e8e2 !important;
  border-radius: 8px !important;
  max-height: 460px;
  overflow-y: auto;
}

.compact-info {
  background: #ffffff;
  border: 1px solid #e2e8e2 !important;
  border-radius: 8px !important;
}

.info-row {
  display: flex;
  padding: 6px 0;
  border-bottom: 1px solid #f1f1f1;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 12px;
  color: #757575;
  min-width: 84px;
  padding-right: 8px;
  flex-shrink: 0;
}

.info-value {
  font-size: 12px;
  color: #212121;
  font-weight: 500;
  flex: 1;
}

.info-value-full {
  margin-left: 0;
  word-break: break-word;
}

.info-row-audio {
  align-items: center;
}

.info-value-audio {
  display: flex;
  align-items: center;
}

.inline-audio-player {
  width: 100%;
  max-width: 230px;
  height: 30px;
}

.original-request-card {
  background: #fff5f5;
  border-color: #ffd3d3 !important;
}

.pending-request-indicator {
  font-size: 12px;
  font-weight: 600;
  color: #b71c1c;
  background: #fff3e0;
  border: 1px solid #ffcc80;
  border-radius: 6px;
  padding: 6px 8px;
}

.comment-entry {
  border: 1px solid #ececec;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 8px;
  background: white;
  text-align: left !important;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.comment-entry:last-child {
  margin-bottom: 0;
}

.comment-meta {
  font-size: 12px;
  color: #616161;
  margin-bottom: 4px;
  text-align: left !important;
  width: 100%;
}

.comment-caller {
  font-size: 12px;
  margin-top: 4px;
  margin-bottom: 0;
  text-align: left !important;
  width: 100%;
}

.comment-text {
  font-size: 13px;
  white-space: normal;
  text-align: left !important;
  line-height: 1.4;
  width: 100%;
  margin-top: 8px;
  word-break: break-word;
}

.comment-audio {
  width: 100%;
  min-height: 36px;
}

.comment-awaiting {
  font-size: 11px;
  font-weight: 600;
  color: #b26a00;
  background: #fff8e1;
  border: 1px solid #ffd54f;
  border-radius: 4px;
  padding: 2px 6px;
}

.period-helper-text {
  line-height: 1.35;
  padding-left: 2px;
}

.period-section-card {
  background: #fcfcfc;
  border-color: #e9e9e9 !important;
}

.period-title {
  color: #5f5f5f;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.route-selection-wrapper {
  width: 100%;
}

.route-selection-main {
  display: flex;
  align-items: center;
}

.route-selection-driver {
  font-size: 0.75rem;
  color: #757575;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.route-detail-chip {
  background-color: #e0e0e0;
  color: #000;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  margin-left: 8px;
  display: inline-block;
}

.route-list-item {
  padding-top: 0px;
  padding-bottom: 0px;
  padding-left: 8px;
  transition: all 0.2s ease;
}

.route-list-item:hover {
  background-color: rgba(92, 184, 96, 0.05);
}

.save-btn {
  min-width: 120px;
}

/* Dialog Header Styles */
.dialog-header {
  background: linear-gradient(135deg, rgb(92, 184, 96) 0%, #388e3c 100%);
  color: white;
  padding: 0px 16px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 400;
}

/* Account Search Styles */
.search-toggle {
  height: 32px !important;
  border-radius: 6px;
}

.search-toggle .v-btn {
  font-size: 13px;
  padding: 6px 12px !important;
  min-width: 70px;
}

.search-toggle.v-btn-toggle .v-btn.v-btn--active {
  background-color: rgb(92, 184, 96) !important;
  color: white !important;
}

.account-list-container {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
}

.account-list {
  background: transparent;
}

.account-item {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #eeeeee;
}

.account-item:hover {
  background-color: #e8f5e9 !important;
}

.account-item:last-child {
  border-bottom: none;
}

.delete-warning-banner {
  border-radius: 8px;
}

.delete-summary-card {
  border: 1px solid #ffcdd2;
  background: #fff5f5;
  border-radius: 10px;
  padding: 10px 12px;
}

.delete-summary-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #c62828;
  margin-bottom: 8px;
}

.delete-summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 12px;
}

.delete-summary-item--full {
  grid-column: 1 / -1;
}

.delete-label {
  font-size: 11px;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.delete-value {
  font-size: 13px;
  color: #212121;
  font-weight: 500;
  line-height: 1.35;
}
</style>
